import React, { useState, useEffect, useCallback } from "react";
import TaskForm from "./TaskForm";
import { jwtDecode } from "jwt-decode";
import UpdateStatus from "./UpdateStatus";
import Request from "./Request";
import Response from "./Response";
import ShiftedTasksHistoryModal from "./ShiftedTasksHistoryModal";
import ShiftTaskModal from "./ShiftTaskModal";
import Snackbar from "../Components/Snackbar";
import ConfirmDialog from "../Components/ConfirmDialog";

const TaskDisplay = ({ onEditTask, onCreateTask }) => {
  // State management
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [filterDateRange, setFilterDateRange] = useState("all");

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(10);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskFormData, setTaskFormData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // Add this to your component's state
  const [showFilters, setShowFilters] = useState(false);

  // Additional state for related data
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Add custom date range states
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [filterRecurringType, setFilterRecurringType] = useState("all");

  // Custom mobile dropdown states
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  // Modal states

  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);

  // ENHANCED: Notification states
  const [showResponsePage, setShowResponsePage] = useState(false);
  const [incomingRequestCount, setIncomingRequestCount] = useState(0);
  const [responseUpdateCount, setResponseUpdateCount] = useState(0);
  const [requestRefreshKey, setRequestRefreshKey] = useState(0);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info'
  });

  // Confirm Dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null
  });

  const [dialogLoading, setDialogLoading] = useState(false);
  const [showShiftedTasksHistory, setShowShiftedTasksHistory] = useState(false);
  const [shiftedTaskIds, setShiftedTaskIds] = useState(new Set());

  // State for tracking the selected task for shifting
  const [selectedTaskForShift, setSelectedTaskForShift] = useState(null);
  const [showShiftTaskModal, setShowShiftTaskModal] = useState(false);

  const [accessPermissions, setAccessPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);

  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);

  // Accordion state for mobile cards
  const [expandedMobileTaskIds, setExpandedMobileTaskIds] = useState(new Set());
  const toggleMobileTaskExpand = (taskId, e) => {
    e.stopPropagation();
    setExpandedMobileTaskIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  // Update the handleTaskClick function
  const handleTaskClick = (task, event) => {
    // Prevent opening detail modal if checkbox was clicked
    if (
      event &&
      (event.target.type === "checkbox" ||
        event.target.closest('input[type="checkbox"]'))
    ) {
      return;
    }
    setSelectedTaskDetail(task);
    setShowTaskDetail(true);
  };

  // Handler function to close task detail modal
  const handleCloseTaskDetail = () => {
    setShowTaskDetail(false);
    setSelectedTaskDetail(null);
  };

  const [shiftingMode, setShiftingMode] = useState("individual");
  const [currentShiftIndex, setCurrentShiftIndex] = useState(0);
  const [shiftResults, setShiftResults] = useState([]);
  const [showShiftProgress, setShowShiftProgress] = useState(false);
  const [isShiftingInProgress, setIsShiftingInProgress] = useState(false);
  // Enhanced Employee Filtering with Department-based Logic
  const [filteredEmployeesForFilter, setFilteredEmployeesForFilter] = useState(
    []
  );

  // State
  const [filterEmployee, setFilterEmployee] = useState("all");

  const [selectedTasks, setSelectedTasks] = useState([]);
  // NEW: Recurring type toggle buttons configuration
  const recurringTypeTabs = [
    { id: "all", label: "All Tasks", icon: "🌐" },
    { id: "recurring", label: "Recurring", icon: "🔄" },
    { id: "shifted", label: "Shifted", icon: "⇄" },
  ];

  const handleTaskSelection = (taskId, event) => {
    if (event) {
      event.stopPropagation();
    }

    const task = tasks.find((t) => t._id === taskId);

    // Prevent selection of completed or cancelled tasks
    if (
      task &&
      (task.status === "completed" ||
        task.status === "cancelled" ||
        task.status === "late-complete" ||
        task.status === "re-late-complete")
    ) {
      if (window.toast) {
        window.toast.warning("Completed and cancelled tasks cannot be shifted");
      }
      return;
    }

    setSelectedTasks((prev) => {
      if (prev.includes(taskId)) {
        return prev.filter((id) => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredTasks = getFilteredAndSortedTasks();
    const shiftableTasks = filteredTasks.filter(
      (task) =>
        task.status !== "completed" &&
        task.status !== "cancelled" &&
        task.status !== "late-complete" &&
        task.status !== "re-late-complete"
    );

    if (selectedTasks.length === shiftableTasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(shiftableTasks.map((task) => task._id));
    }
  };

  // 🚀 NEW: Individual shift handler with progress tracking
  const handleIndividualShift = async (tasksToShift, employeeAssignments) => {
    setIsShiftingInProgress(true);
    setShowShiftProgress(true);
    setShiftResults([]);
    setCurrentShiftIndex(0);

    const results = [];

    for (let i = 0; i < tasksToShift.length; i++) {
      const task = tasksToShift[i];
      const targetEmployee = employeeAssignments[task._id];

      if (!targetEmployee) {
        results.push({
          taskId: task._id,
          taskTitle: task.title,
          success: false,
          error: "No target employee selected",
        });
        continue;
      }

      setCurrentShiftIndex(i + 1);

      try {
        const token = authToken;

        const shiftData = {
          taskId: task._id,
          fromEmployeeId: Array.isArray(task.assignedTo)
            ? task.assignedTo[0]
            : task.assignedTo,
          toEmployeeId: targetEmployee.id,
          reason: `Individual shift - Task ${i + 1} of ${tasksToShift.length}`,
          shiftDate: new Date().toISOString(),
          shiftType: "individual",
        };

        const response = await fetch(`${API_BASE_URL}/task/shift`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-auth-token": token,
          },
          credentials: "include",
          body: JSON.stringify(shiftData),
        });

        const responseData = await response.json();

        if (response.ok) {
          results.push({
            taskId: task._id,
            taskTitle: task.title,
            success: true,
            targetEmployee: targetEmployee.name,
            shiftId: responseData.shiftId || responseData._id,
          });
        } else {
          results.push({
            taskId: task._id,
            taskTitle: task.title,
            success: false,
            error: responseData.message || "Shift failed",
          });
        }
      } catch (error) {
        results.push({
          taskId: task._id,
          taskTitle: task.title,
          success: false,
          error: error.message,
        });
      }

      // Small delay between shifts for better UX
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setShiftResults(results);
    setIsShiftingInProgress(false);

    // Show summary
    const successCount = results.filter((r) => r.success).length;
    const failureCount = results.length - successCount;

    if (window.toast) {
      if (successCount > 0 && failureCount === 0) {
        window.toast.success(`Successfully shifted ${successCount} tasks`);
      } else if (successCount > 0 && failureCount > 0) {
        window.toast.warning(
          `Shifted ${successCount} tasks, ${failureCount} failed`
        );
      } else {
        window.toast.error(`Failed to shift all ${failureCount} tasks`);
      }
    }

    // Refresh tasks and clear selection after completion
    setTimeout(() => {
      fetchTasks();
      fetchShiftedTaskIds();
      setSelectedTasks([]);
      setShowShiftProgress(false);
    }, 3000);
  };

  // 🚀 ENHANCED: Enhanced shift tasks handler
  const handleShiftTasks = () => {
    if (selectedTasks.length === 0) {
      if (window.toast) {
        window.toast.error("Please select at least one task to shift");
      }
      return;
    }

    const tasksToShift = tasks.filter(
      (task) =>
        selectedTasks.includes(task._id) &&
        task.status !== "completed" &&
        task.status !== "cancelled"
    );

    if (tasksToShift.length === 0) {
      if (window.toast) {
        window.toast.error(
          "No shiftable tasks selected. Completed tasks cannot be shifted."
        );
      }
      return;
    }

    const completedTasks = tasks.filter(
      (task) =>
        selectedTasks.includes(task._id) &&
        (task.status === "completed" || task.status === "cancelled")
    );

    if (completedTasks.length > 0) {
      if (window.toast) {
        window.toast.warning(
          `${completedTasks.length} completed/cancelled tasks were excluded from selection`
        );
      }
      setSelectedTasks((prev) =>
        prev.filter(
          (taskId) =>
            !completedTasks.some((completed) => completed._id === taskId)
        )
      );
    }

    setSelectedTaskForShift(tasksToShift);
    setShowShiftTaskModal(true);
  };

  const handleShiftSuccess = () => {
    setShowShiftTaskModal(false);
    setSelectedTaskForShift(null);
    setSelectedTasks([]);
    fetchTasks();
  };

  // Date range tabs configuration
  const dateRangeTabs = [
    { id: "all", label: "All Time", icon: "🌐" },
    { id: "yesterday", label: "Yesterday", icon: "◀️" },
    { id: "today", label: "Today", icon: "📅" },
    { id: "tomorrow", label: "Tomorrow", icon: "⭐" },
    { id: "thisWeek", label: "This Week", icon: "📊" },
    { id: "thisMonth", label: "This Month", icon: "📈" },
    { id: "reopen", label: "Re-opened", icon: "🔄" },
    { id: "custom", label: "Custom Range", icon: "📆" },
  ];

  const fetchEmployees = useCallback(async () => {
    try {
      const companyId = await getCompanyId();
      const token = authToken;

      if (!companyId || !token) return;

      const response = await fetch(
        `${API_BASE_URL}/employees/company/${companyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-auth-token": token,
          },
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        let employeeList = [];

        if (Array.isArray(data)) {
          employeeList = data;
        } else if (data && Array.isArray(data.employees)) {
          employeeList = data.employees;
        } else if (data && Array.isArray(data.data)) {
          employeeList = data.data;
        }

        setEmployees(employeeList.filter(emp => emp.isActive !== false));
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  }, []);

  useEffect(() => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const decoded = jwtDecode(token);
      setAccessPermissions(decoded.accessPermissions || []);
      setUserRole(decoded.role || null);
    } catch (error) {
      console.error("Failed to decode token:", error);
    }
  }, []);

  // API base URL
  const API_BASE_URL = "https://greenyellow-nightingale-241896.hostingersite.com/api";

  // Helper to format file size
  const formatFileSize = (size) => {
    if (!size) return "Unknown size";
    if (size < 1024) return `${size} bytes`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Render attachments in task detail modal
  const renderAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return <div>No attachments</div>;
    const getAttachmentName = (att) => {
      if (att.name) return att.name;
      if (att.filename) return att.filename;
      if (att.originalname) return att.originalname;
      if (att.url) {
        const parts = att.url.split('/');
        const last = parts[parts.length - 1];
        return last || "Unnamed File";
      }
      return "Unnamed File";
    };
    return (
      <div className="space-y-2">
        {attachments.map((att, idx) => (
          <div key={idx} className="flex items-center space-x-3 border p-2 rounded bg-gray-50">
            <span className="inline-block w-6 text-center">
              {att.type?.startsWith("image") ? (
                <img src={`https://greenyellow-nightingale-241896.hostingersite.com/${att.url}`} alt={getAttachmentName(att)} className="w-6 h-6 object-cover rounded" />
              ) : att.type?.startsWith("audio") ? (
                <span role="img" aria-label="audio">🔊</span>
              ) : (
                <span role="img" aria-label="file">📎</span>
              )}
            </span>
            <span className="font-medium">{getAttachmentName(att)}</span>
            <span className="text-xs text-gray-500">{formatFileSize(att.size)}</span>
            <span className="text-xs text-gray-400">{att.type}</span>
            {att.type?.startsWith("image") ? (
              <a href={`https://greenyellow-nightingale-241896.hostingersite.com/${att.url}`} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 underline">Preview</a>
            ) : att.type?.startsWith("audio") ? (
              <audio controls src={`https://greenyellow-nightingale-241896.hostingersite.com/${att.url}`} className="ml-2 h-6" />
            ) : (
              <a href={`https://greenyellow-nightingale-241896.hostingersite.com/${att.url}`} download className="ml-2 text-blue-600 underline">Download</a>
            )}
          </div>
        ))}
      </div>
    );
  };
  const authToken = localStorage
    .getItem("authToken")
    ?.replace(/['"]/g, "")
    .trim();

  // Utility functions
  // Enhanced getCompanyId function that handles both company and employee logins
  const getCompanyId = useCallback(async () => {
    console.group("⚙️ [DEBUG] getCompanyId() - Starting company ID resolution");

    const token = localStorage.getItem("authToken");
    console.log(
      "📋 Token from localStorage:",
      token ? `${token.substring(0, 50)}...` : "null"
    );

    if (token) {
      try {
        console.log("⚙️ Attempting to decode JWT token...");
        const decodedToken = jwtDecode(token);
        console.log(
          "📊 Decoded token structure:",
          JSON.stringify(decodedToken, null, 2)
        );

        // New logic based on role
        if (decodedToken && decodedToken.role === "company") {
          // If role is company, use id as companyId
          if (decodedToken.id) {
            console.groupEnd();
            return decodedToken.id;
          }
        } else {
          // Otherwise, use companyId field if available
          if (decodedToken && decodedToken.companyId) {
            console.groupEnd();
            return decodedToken.companyId;
          }
        }

        // If role is not company and companyId is not present, try userId/id to fetch profile
        if (decodedToken && (decodedToken.userId || decodedToken.id)) {
          const userId = decodedToken.userId || decodedToken.id;
          console.log("👤 Employee ID from JWT:", userId);
          console.log(
            "🔄 Attempting to fetch employee profile from MongoDB..."
          );
        } else {
          console.warn("⚠️ No userId or id found in JWT token");
          console.log(
            "⚙️ Token payload keys available:",
            Object.keys(decodedToken || {})
          );
        }
      } catch (error) {
        console.error("❌ JWT decoding failed:", error);
        console.warn("🔄 Trying alternative methods...");
      }
    } else {
      console.warn("⚠️ No authToken found in localStorage");
      console.log("📋 All localStorage keys:", Object.keys(localStorage));
    }

    // Fallback: try localStorage
    const storedCompanyId = localStorage.getItem("companyId");
    if (storedCompanyId) {
      console.log("✅ Found companyId in localStorage:", storedCompanyId);
      console.groupEnd();
      return storedCompanyId;
    } else {
      console.warn("⚠️ No companyId found in localStorage");
    }

    console.error("❌ Unable to resolve company ID from any source");
    console.groupEnd();
    return null;
  }, []);

  // Cache for company names to avoid repeat calls
  const [companyCache, setCompanyCache] = useState({});

  // Fetch company details by ID and cache the result
  const fetchCompanyDetails = useCallback(
    async (companyId) => {
      if (!companyId) return null;

      // Return from cache if present
      if (companyCache[companyId]) return companyCache[companyId];

      try {
        const token = authToken; // existing token string in your file
        if (!token) return null;

        const res = await fetch(`${API_BASE_URL}/companies/${companyId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-auth-token": token,
          },
          credentials: "include",
        });

        if (!res.ok) return null;

        const data = await res.json();
        const name = data.businessName || data.name || "Company";

        setCompanyCache((prev) => ({ ...prev, [companyId]: name }));
        return name;
      } catch (e) {
        console.error("Error fetching company details:", e);
        return null;
      }
    },
    [API_BASE_URL, authToken, companyCache]
  );

  // Updated getCurrentUserId function
  const getCurrentUserId = useCallback(() => {
    let userId = null;
    let foundKey = null;

    // 1. Prioritize extracting user ID directly from JWT authToken (Source of Truth)
    try {
      const token = localStorage.getItem("authToken")?.replace(/['"]/g, "").trim();
      if (token) {
        const decoded = jwtDecode(token);
        let jwtUserId = decoded.userId || decoded.id || decoded.employeeId || decoded.user?.id || decoded._id;
        if (jwtUserId && typeof jwtUserId === "object") {
          jwtUserId = jwtUserId._id || jwtUserId.id;
        }
        if (jwtUserId) {
          userId = String(jwtUserId).replace(/['"]/g, "").trim();
          foundKey = "JWT";
        }
      }
    } catch (error) {
      console.error("❌ Failed to extract userId from JWT:", error);
    }

    // 2. Fallback to localStorage keys only if JWT token missing/invalid
    if (!userId) {
      const possibleKeys = [
        "userId",
        "user_id",
        "id",
        "employeeId",
        "employee_id",
      ];
      for (const key of possibleKeys) {
        const value = localStorage.getItem(key);
        if (value) {
          userId = String(value).replace(/['"]/g, "").trim();
          foundKey = key;
          break;
        }
      }
    }

    if (!userId) {
      console.warn("⚠️ No user ID found in localStorage or JWT");
    } else {
      console.log("🎯 Using userId from:", foundKey);
    }

    return userId;
  }, []);

  // Helper function to check permissions
  const hasPermission = (permission) => {
    // If user role is 'company', allow all permissions
    if (userRole === "company") {
      return true;
    }
    // Otherwise, check accessPermissions array
    return accessPermissions.includes(permission);
  };

  // Enhanced overdue calculation that considers exact date and time
  const calculateOverdueTime = (endDateTime, completedAt = null) => {
    if (!endDateTime)
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        isOverdue: false,
        totalMinutes: 0,
      };

    try {
      const deadline = new Date(endDateTime);

      // If completedAt provided and task was completed before now, use completedAt for overdue calc
      let referenceTime = new Date();
      if (completedAt) {
        try {
          const comp = new Date(completedAt);
          // If completed time is valid, use it as the reference time
          if (!isNaN(comp.getTime())) {
            referenceTime = comp;
          }
        } catch (e) {
          // fallback to now
          referenceTime = new Date();
        }
      }

      // Calculate the exact time difference in milliseconds
      const diffTime = referenceTime - deadline;

      // If difference is negative, task is not overdue at reference time
      if (diffTime <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          isOverdue: false,
          totalMinutes: 0,
        };
      }

      // Calculate overdue time in different units
      const totalMinutes = Math.floor(diffTime / (1000 * 60));
      const totalHours = Math.floor(diffTime / (1000 * 60 * 60));
      const totalDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      // Calculate remaining hours and minutes after removing full days
      const remainingHours = totalHours % 24;
      const remainingMinutes = totalMinutes % 60;

      return {
        days: totalDays,
        hours: remainingHours,
        minutes: remainingMinutes,
        isOverdue: true,
        totalMinutes: totalMinutes,
        totalHours: totalHours,
        totalDays: totalDays,
      };
    } catch (error) {
      console.error("Error calculating overdue time:", error);
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        isOverdue: false,
        totalMinutes: 0,
      };
    }
  };

  // Backward compatibility - returns just the days for existing code
  const calculateOverdueDays = (endDateTime) => {
    const overdueInfo = calculateOverdueTime(endDateTime);
    return overdueInfo.days;
  };

  // Enhanced function to check if task is overdue (considering exact time)
  const isTaskOverdue = (task) => {
    if (!task.endDateTime) return false;

    const now = new Date();
    const deadline = new Date(task.endDateTime);

    // Task is overdue if current time is past the deadline and task is not completed/cancelled
    return (
      now > deadline &&
      task.status !== "completed" &&
      task.status !== "cancelled"
    );
  };

  // Function to format overdue time in a human-readable way
  const formatOverdueTime = (endDateTime, completedAt = null) => {
    const overdueInfo = calculateOverdueTime(endDateTime, completedAt);

    if (!overdueInfo.isOverdue) {
      return "Not overdue";
    }

    const { days, hours, minutes } = overdueInfo;

    if (days > 0) {
      if (hours > 0) {
        return `${days} day${days !== 1 ? "s" : ""}, ${hours} hour${hours !== 1 ? "s" : ""
          }`;
      }
      return `${days} day${days !== 1 ? "s" : ""}`;
    } else if (hours > 0) {
      if (minutes > 0) {
        return `${hours} hour${hours !== 1 ? "s" : ""}, ${minutes} minute${minutes !== 1 ? "s" : ""
          }`;
      }
      return `${hours} hour${hours !== 1 ? "s" : ""}`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
    } else {
      return "Just overdue";
    }
  };

  const getStatusColor = (status, task) => {
    const isParentTemplate = task.repeat && !task.isRecurringInstance;

    // Check if task is overdue first
    if (isTaskOverdue(task)) {
      return "bg-gradient-to-r from-red-600 to-red-700 text-white border-red-600 ring-2 ring-red-200";
    }

    const baseColor = (() => {
      switch (status?.toLowerCase()) {
        case "completed":
          return "bg-green-100 text-green-800 border-green-200";
        case "in-progress":
          return "bg-blue-100 text-blue-800 border-blue-200";
        case "pending":
          return "bg-yellow-100 text-yellow-800 border-yellow-200";
        case "cancelled":
          return "bg-red-100 text-red-800 border-red-200";
        case "reopened":
          return "bg-purple-100 text-purple-800 border-purple-200";
        case "overdue":
          return "bg-gradient-to-r from-red-600 to-red-700 text-white border-red-600 ring-2 ring-red-200";
        default:
          return "bg-gray-100 text-gray-800 border-gray-200";
      }
    })();

    if (isParentTemplate) {
      // NEW: Subtle gray for parents (templates)
      return `${baseColor} opacity-70 bg-gray-50 border-gray-300 relative`;
    } else if (task.isRecurringInstance) {
      return `${baseColor} relative`; // Chain icon positioning
    }

    return baseColor;
  };

  // UPDATED: Enhanced getStatusDisplay - Special handling for parents (template status)
  const getStatusDisplay = (task) => {
    const originalStatus = task.status || "pending";
    const isOverdue = isTaskOverdue(task);
    const overdueDays = isOverdue ? calculateOverdueDays(task.endDateTime) : 0;

    let displayText = originalStatus;
    let recurringInfo = null;
    let isParentTemplate = task.repeat && !task.isRecurringInstance;

    if (isParentTemplate) {
      // For parents: Show as "Template" with series info
      recurringInfo = `Recurring Template`;
      displayText = `Pending (Template - ${task.repeatFrequency || "Daily"})`;
    } else if (task.isRecurringInstance) {
      // For instances: Parent reference + frequency
      const frequency = task.repeatFrequency || "";
      recurringInfo = `Recurring ${frequency.charAt(0).toUpperCase() + frequency.slice(1)
        }`;
      displayText = `${originalStatus}`;
    }

    return {
      originalStatus,
      isOverdue,
      overdueDays,
      displayText,
      overdueBadge: isOverdue
        ? `${overdueDays} day${overdueDays !== 1 ? "s" : ""} overdue`
        : null,
      recurringInfo,
      isParentTemplate, // NEW: Flag for UI (e.g., special badge)
      isShifted: !!(
        task.shifted ||
        task.isShifted ||
        task.shiftedBy ||
        task.reassignedBy ||
        task.shifted_by ||
        task.oldAssignee ||
        task.reassigned ||
        task.isReassigned ||
        task.shift_date ||
        task.is_shifted ||
        task.status === 'reassigned' ||
        task.was_shifted ||
        task.was_reassigned ||
        task.is_shift ||
        task.isShift ||
        task.shift ||
        task.is_reassigned ||
        task.reassigned_at ||
        task.shifted_at ||
        task.old_assigned_to ||
        task.previous_assignee ||
        shiftedTaskIds.has(String(task._id)) ||
        shiftedTaskIds.has(String(task.id)) ||
        (task.history && task.history.some(h => h.status === 'reassigned' || h.type === 'shift'))
      )
    };
  };

  // ENHANCED: Fetch both incoming requests and response updates
  const fetchNotificationCounts = useCallback(async () => {
    try {
      // Don't set loading state to prevent UI flickering during background refreshes
      // setLoadingRequests(true);
      const companyId = await getCompanyId();
      const currentUserId = getCurrentUserId();
      const token = authToken;

      if (!companyId || !token || !currentUserId) {
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/requests?companyId=${companyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "x-auth-token": token,
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch requests");
      }

      const data = await response.json();
      const requestsList = Array.isArray(data)
        ? data
        : data.data || data.requests || [];

      // 1. Incoming requests (requests TO current user that are pending)
      const incomingPending = requestsList.filter(
        (request) =>
          String(request.requestTo) === String(currentUserId) &&
          request.status === "pending"
      );

      // 2. Response updates (requests FROM current user that have been updated)
      const lastCheckedTime = localStorage.getItem("lastNotificationCheck");
      const checkTime = lastCheckedTime
        ? new Date(lastCheckedTime)
        : new Date(Date.now() - 24 * 60 * 60 * 1000);

      const responseUpdates = requestsList.filter((request) => {
        const isMyRequest =
          String(request.requestFrom) === String(currentUserId);
        const hasUpdate = ["accepted", "rejected", "resolved"].includes(
          request.status
        );
        const isRecentUpdate =
          request.updatedAt && new Date(request.updatedAt) > checkTime;

        return isMyRequest && hasUpdate && isRecentUpdate;
      });

      setIncomingRequestCount(incomingPending.length);
      setResponseUpdateCount(responseUpdates.length);

      // Increment refresh key to trigger re-fetch in child components
      setRequestRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Error fetching notification counts:", error);
    } finally {
      // setLoadingRequests(false);
    }
  }, [API_BASE_URL, authToken, getCurrentUserId]);

  // ENHANCED: Notification bell component with dual badges
  const EnhancedNotificationBell = () => {
    const totalCount = incomingRequestCount + responseUpdateCount;

    return (
      <div className="relative group">
        <button
          onClick={() => {
            setShowResponsePage(true);
            localStorage.setItem(
              "lastNotificationCheck",
              new Date().toISOString()
            );
          }}
          className="relative flex flex-col items-center bg-[rgba(59,130,246,0.28)] sm:bg-white hover:bg-[rgba(59,130,246,0.38)] sm:hover:bg-gray-50 border border-[rgba(96,165,250,0.6)] sm:border-2 sm:border-gray-200 hover:border-[rgba(96,165,250,0.8)] sm:hover:border-blue-400 rounded-2xl px-2 pt-2 pb-1 transition-all duration-300 shadow-[0_4px_15px_rgba(59,130,246,0.18)] sm:shadow-sm hover:shadow-[0_4px_20px_rgba(59,130,246,0.3)] sm:hover:shadow-lg transform hover:-translate-y-1 hover:scale-105 backdrop-blur-[8px] sm:backdrop-blur-none"
        >
          <div className="relative">
            <svg
              className={`w-6 h-6 text-white sm:text-gray-600 group-hover:text-white sm:group-hover:text-blue-600 transition-colors duration-300 ${totalCount > 0 ? "animate-swing" : ""
                }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>

            {/* Incoming Requests Badge (Top Right - Red) */}
            {incomingRequestCount > 0 && (
              <>
                <div className="absolute -top-2 -right-2 min-w-[20px] h-5 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center px-1 shadow-lg animate-pulse z-10">
                  <span className="text-[10px]">
                    {incomingRequestCount > 99 ? "99+" : incomingRequestCount}
                  </span>
                </div>
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full animate-ping opacity-75"></div>
              </>
            )}

            {/* Response Updates Badge (Top Left - Green) */}
            {responseUpdateCount > 0 && (
              <>
                <div className="absolute -top-2 -left-2 min-w-[20px] h-5 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-bold rounded-full flex items-center justify-center px-1 shadow-lg animate-pulse z-10">
                  <span className="text-[10px]">
                    {responseUpdateCount > 99 ? "99+" : responseUpdateCount}
                  </span>
                </div>
                <div className="absolute -top-2 -left-2 w-5 h-5 bg-green-500 rounded-full animate-ping opacity-75"></div>
              </>
            )}

            {/* Combined Badge (when both are present) */}
            {incomingRequestCount > 0 && responseUpdateCount > 0 && (
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 min-w-[16px] h-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-bold rounded-full flex items-center justify-center px-1 shadow-md">
                <span className="text-[8px]">
                  {totalCount > 99 ? "99+" : totalCount}
                </span>
              </div>
            )}
          </div>

          {/* Label */}
          <span className="text-[10px] font-bold text-white sm:text-gray-600 group-hover:text-white sm:group-hover:text-blue-600 transition-colors duration-300 mt-0.5 leading-none">
            Request
          </span>

          {/* Loading indicator */}
          {loadingRequests && (
            <div className="absolute inset-0 bg-[#0c1a4e]/85 sm:bg-white/75 rounded-2xl flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </button>

        {/* Enhanced tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap z-20 pointer-events-none">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span>{incomingRequestCount} pending requests</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>{responseUpdateCount} response updates</span>
            </div>
          </div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      </div>
    );
  };
  const ResponseUpdatesTab = () => {
    const [myRequestsWithUpdates, setMyRequestsWithUpdates] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchMyUpdatedRequests = async () => {
        try {
          setLoading(true);
          const companyId = await getCompanyId();
          const currentUserId = getCurrentUserId();
          const token = authToken;

          if (!companyId || !token || !currentUserId) {
            return;
          }

          const response = await fetch(
            `${API_BASE_URL}/requests?companyId=${companyId}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
                "x-auth-token": token,
              },
              credentials: "include",
            }
          );

          if (!response.ok) {
            throw new Error("Failed to fetch requests");
          }

          const data = await response.json();
          const requestsList = Array.isArray(data)
            ? data
            : data.data || data.requests || [];

          // Show both:
          // 1. Requests I sent that got responses (resolved/rejected)
          // 2. Requests sent TO me that I resolved/rejected
          const myUpdatedRequests = requestsList.filter((request) => {
            const iSentIt = String(request.requestFrom) === String(currentUserId);
            const sentToMe = String(request.requestTo) === String(currentUserId);
            const hasResponse = ["accepted", "rejected", "resolved"].includes(request.status);

            return hasResponse && (iSentIt || sentToMe);
          });

          setMyRequestsWithUpdates(myUpdatedRequests);
        } catch (error) {
          console.error("Error fetching updated requests:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchMyUpdatedRequests();
    }, []); // Empty deps OK - component remounts via key prop to refresh

    const formatDate = (dateString, includeTime = true) => {
      if (!dateString) return "Not set";

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "Invalid date";

        if (includeTime) {
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZoneName: "short",
          });
        } else {
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        }
      } catch (error) {
        console.error("Date formatting error:", error);
        return "Invalid date";
      }
    };

    // Enhanced format relative time for better UX
    const formatRelativeTime = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const now = new Date();
        const date = new Date(dateString);
        const diffInMs = now - date;
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMinutes < 60) {
          return diffInMinutes <= 1 ? "Just now" : `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
          return `${diffInHours}h ago`;
        } else if (diffInDays < 7) {
          return `${diffInDays}d ago`;
        } else {
          return formatDate(dateString, false);
        }
      } catch (error) {
        console.error("Relative time formatting error:", error);
        return "Invalid Date";
      }
    };

    // Calculate resolution duration
    const getResolutionDuration = (createdAt, resolvedAt) => {
      if (!createdAt || !resolvedAt) return null;

      try {
        const created = new Date(createdAt);
        const resolved = new Date(resolvedAt);
        const diffInMs = resolved - created;
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInDays > 0) {
          return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ${diffInHours % 24
            }h`;
        } else if (diffInHours > 0) {
          return `${diffInHours} hour${diffInHours > 1 ? "s" : ""}`;
        } else {
          const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
          return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""}`;
        }
      } catch (error) {
        console.error("Duration calculation error:", error);
        return null;
      }
    };

    // Enhanced status icons with better styling
    const getStatusIcon = (status) => {
      switch (status) {
        case "accepted":
          return (
            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center shadow-sm">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          );
        case "rejected":
          return (
            <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center shadow-sm">
              <svg
                className="w-5 h-5 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          );
        case "resolved":
          return (
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-100 to-green-200 rounded-xl flex items-center justify-center shadow-sm">
              <svg
                className="w-5 h-5 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          );
        default:
          return null;
      }
    };

    // Enhanced status colors with gradients
    const getStatusColor = (status) => {
      switch (status) {
        case "accepted":
          return "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-800 border-blue-200 ring-1 ring-blue-200";
        case "rejected":
          return "bg-gradient-to-r from-red-50 to-rose-50 text-red-800 border-red-200 ring-1 ring-red-200";
        case "resolved":
          return "bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-800 border-emerald-200 ring-1 ring-emerald-200";
        default:
          return "bg-gradient-to-r from-slate-50 to-gray-50 text-slate-800 border-slate-200 ring-1 ring-slate-200";
      }
    };

    // Enhanced loading state
    if (loading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-blue-600 mx-auto mb-6"></div>
              <div className="absolute inset-0 rounded-full h-16 w-16 border-4 border-transparent border-r-blue-400 animate-pulse mx-auto"></div>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">
              Loading Response Updates
            </h3>
            <p className="text-slate-600 max-w-sm">
              Fetching updates on your submitted requests...
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Compact Header */}
          <div className="mb-6">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-2xl p-6 text-white shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold mb-1 tracking-tight">
                    Response Updates
                  </h1>
                  <p className="text-blue-100 text-sm">
                    Track the status of your submitted requests
                  </p>
                </div>
                <div className="hidden lg:block">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Compact Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-xs font-medium">
                        Total Updates
                      </p>
                      <p className="text-2xl font-bold">
                        {myRequestsWithUpdates.length}
                      </p>
                    </div>
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-blue-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-xs font-medium">
                        Resolved
                      </p>
                      <p className="text-2xl font-bold">
                        {
                          myRequestsWithUpdates.filter(
                            (r) => r.status === "resolved"
                          ).length
                        }
                      </p>
                    </div>
                    <div className="w-8 h-8 bg-emerald-500 bg-opacity-30 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-emerald-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-xs font-medium">
                        Pending Action
                      </p>
                      <p className="text-2xl font-bold">
                        {
                          myRequestsWithUpdates.filter((r) =>
                            ["accepted", "rejected"].includes(r.status)
                          ).length
                        }
                      </p>
                    </div>
                    <div className="w-8 h-8 bg-amber-500 bg-opacity-30 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-amber-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Compact Empty State */}
          {myRequestsWithUpdates.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-auto">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  No Response Updates
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Your submitted requests haven't received any responses yet.
                  Updates will appear here once your requests are processed.
                </p>
              </div>
            </div>
          ) : (
            // Compact Requests List
            <div className="space-y-5">
              {myRequestsWithUpdates.map((request) => (
                <div
                  key={request._id}
                  className="bg-white border border-slate-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        {getStatusIcon(request.status)}
                        <div className="flex-1 min-w-0">
                          {/* Compact Request Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                                {request.subject}
                              </h4>
                              <p className="text-slate-600 text-sm mb-3 leading-relaxed line-clamp-2">
                                {request.description}
                              </p>
                            </div>
                            <div className="ml-3">
                              <span
                                className={`px-3 py-1.5 rounded-xl text-xs font-semibold border ${getStatusColor(
                                  request.status
                                )}`}
                              >
                                {request.status.charAt(0).toUpperCase() +
                                  request.status.slice(1)}
                              </span>
                            </div>
                          </div>

                          {/* Compact Timeline Information */}
                          <div className="mb-4">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                              {/* Submitted Date */}
                              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                <div className="flex items-center space-x-1.5 mb-1">
                                  <svg
                                    className="w-3 h-3 text-slate-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M12 4v16m8-8H4"
                                    />
                                  </svg>
                                  <span className="text-xs font-medium text-slate-600">
                                    Submitted
                                  </span>
                                </div>
                                <p className="text-xs text-slate-800 font-medium">
                                  {formatDate(request.createdAt)}
                                </p>
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                  {formatRelativeTime(request.createdAt)}
                                </p>
                              </div>

                              {/* Updated Date */}
                              {request.updatedAt && (
                                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                                  <div className="flex items-center space-x-1.5 mb-1">
                                    <svg
                                      className="w-3 h-3 text-blue-500"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                      />
                                    </svg>
                                    <span className="text-xs font-medium text-blue-600">
                                      Updated
                                    </span>
                                  </div>
                                  <p className="text-xs text-blue-800 font-medium">
                                    {formatDate(request.updatedAt)}
                                  </p>
                                  <p className="text-[10px] text-blue-500 mt-0.5">
                                    {formatRelativeTime(request.updatedAt)}
                                  </p>
                                </div>
                              )}

                              {/* Resolved Date */}
                              {request.status === "resolved" &&
                                request.resolvedAt && (
                                  <>
                                    <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                                      <div className="flex items-center space-x-1.5 mb-1">
                                        <svg
                                          className="w-3 h-3 text-emerald-500"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                        <span className="text-xs font-medium text-emerald-600">
                                          Resolved
                                        </span>
                                      </div>
                                      <p className="text-xs text-emerald-800 font-medium">
                                        {formatDate(request.resolvedAt)}
                                      </p>
                                      <p className="text-[10px] text-emerald-500 mt-0.5">
                                        {formatRelativeTime(request.resolvedAt)}
                                      </p>
                                    </div>

                                    {/* Resolution Duration */}
                                    <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                                      <div className="flex items-center space-x-1.5 mb-1">
                                        <svg
                                          className="w-3 h-3 text-green-500"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M13 10V3L4 14h7v7l9-11h-7z"
                                          />
                                        </svg>
                                        <span className="text-xs font-medium text-green-600">
                                          Duration
                                        </span>
                                      </div>
                                      <p className="text-xs text-green-800 font-medium">
                                        {getResolutionDuration(
                                          request.createdAt,
                                          request.resolvedAt
                                        ) || "N/A"}
                                      </p>
                                      <p className="text-[10px] text-green-500 mt-0.5">
                                        Total time
                                      </p>
                                    </div>
                                  </>
                                )}

                              {/* Handled By Information */}
                              <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                                <div className="flex items-center space-x-1.5 mb-1">
                                  <svg
                                    className="w-3 h-3 text-indigo-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                    />
                                  </svg>
                                  <span className="text-xs font-medium text-indigo-600">
                                    Handled By
                                  </span>
                                </div>
                                <p className="text-xs text-indigo-800 font-medium">
                                  {getEmployeeName(request.requestTo)}
                                </p>
                                <p className="text-[10px] text-indigo-500 mt-0.5">
                                  Assigned
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Compact Resolved Status Highlight */}
                          {request.status === "resolved" &&
                            request.resolvedAt && (
                              <div className="mb-4">
                                <div className="bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-50 border-2 border-emerald-200 rounded-xl p-4 shadow-sm">
                                  <div className="flex items-center space-x-3 mb-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center shadow-md">
                                      <svg
                                        className="w-5 h-5 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                    </div>
                                    <div className="flex-1">
                                      <h4 className="text-base font-bold text-emerald-800 flex items-center">
                                        🎉 Request Successfully Resolved
                                      </h4>
                                      <p className="text-xs text-emerald-600 mt-0.5">
                                        Your request has been completed and
                                        resolved
                                      </p>
                                    </div>
                                  </div>

                                  <div className="bg-white bg-opacity-70 backdrop-blur-sm rounded-lg p-3 border border-emerald-100">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                      <div className="text-center md:text-left">
                                        <div className="flex items-center justify-center md:justify-start space-x-1.5 mb-1">
                                          <svg
                                            className="w-3 h-3 text-emerald-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4h3a1 1 0 011 1v11a2 2 0 01-2 2H6a2 2 0 01-2-2V8a1 1 0 011-1h3z"
                                            />
                                          </svg>
                                          <span className="text-xs font-medium text-emerald-600">
                                            Resolved Date
                                          </span>
                                        </div>
                                        <p className="text-sm font-bold text-emerald-800">
                                          {formatDate(
                                            request.resolvedAt,
                                            false
                                          )}
                                        </p>
                                      </div>

                                      <div className="text-center md:text-left">
                                        <div className="flex items-center justify-center md:justify-start space-x-1.5 mb-1">
                                          <svg
                                            className="w-3 h-3 text-emerald-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                          </svg>
                                          <span className="text-xs font-medium text-emerald-600">
                                            Resolved Time
                                          </span>
                                        </div>
                                        <p className="text-sm font-bold text-emerald-800">
                                          {new Date(
                                            request.resolvedAt
                                          ).toLocaleTimeString("en-US", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: true,
                                            timeZoneName: "short",
                                          })}
                                        </p>
                                      </div>

                                      <div className="text-center md:text-left">
                                        <div className="flex items-center justify-center md:justify-start space-x-1.5 mb-1">
                                          <svg
                                            className="w-3 h-3 text-emerald-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M13 10V3L4 14h7v7l9-11h-7z"
                                            />
                                          </svg>
                                          <span className="text-xs font-medium text-emerald-600">
                                            Resolution Time
                                          </span>
                                        </div>
                                        <p className="text-sm font-bold text-emerald-800">
                                          {getResolutionDuration(
                                            request.createdAt,
                                            request.resolvedAt
                                          ) || "N/A"}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-t border-emerald-200">
                                      <p className="text-center text-[10px] text-emerald-600 font-medium">
                                        ✅ Thank you for your patience •
                                        Resolution completed successfully
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                          {/* Compact Response Reason */}
                          {request.reason && (
                            <div className="mt-4">
                              <div className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-4">
                                <div className="flex items-center space-x-1.5 mb-2">
                                  <svg
                                    className="w-4 h-4 text-slate-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                                    />
                                  </svg>
                                  <h5 className="text-sm font-semibold text-slate-800">
                                    Response Details
                                  </h5>
                                </div>
                                <p className="text-slate-700 text-sm leading-relaxed bg-white bg-opacity-50 rounded-lg p-3">
                                  {request.reason}
                                </p>

                                {/* Response Attachments - Check multiple possible field names */}
                                {(request.responseFile || request.file || request.responseAttachment) && (
                                  <div className="mt-3">
                                    <div className="flex items-center space-x-1.5 mb-2">
                                      <svg
                                        className="w-4 h-4 text-purple-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                        />
                                      </svg>
                                      <span className="text-xs font-medium text-slate-700">
                                        Attachment
                                      </span>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 border border-slate-200 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                                        <svg
                                          className="w-4 h-4 text-slate-400 flex-shrink-0"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                          />
                                        </svg>
                                        <span className="text-xs text-slate-700 truncate font-medium">
                                          Response Attachment
                                        </span>
                                      </div>
                                      <a
                                        href={request.responseFile || request.file || request.responseAttachment}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download
                                        className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 flex-shrink-0"
                                      >
                                        <svg
                                          className="w-3 h-3"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                          />
                                        </svg>
                                        <span>Download</span>
                                      </a>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const EnhancedResponseModal = () => {
    // Remember last active tab or default to incoming
    const [activeTab, setActiveTab] = useState(() => {
      return localStorage.getItem("requestManagementTab") || "incoming";
    });

    // Save tab preference when it changes
    useEffect(() => {
      localStorage.setItem("requestManagementTab", activeTab);
    }, [activeTab]);

    if (!showResponsePage) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col">
          {/* Compact Header with Tabs */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 sm:from-blue-600 sm:to-purple-600 px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-xl font-bold text-white">
                  Request Management
                </h2>
                <p className="text-blue-100 text-sm">
                  Manage requests and view updates
                </p>
              </div>
              <button
                onClick={() => {
                  setShowResponsePage(false);
                  // Removed fetchNotificationCounts() to prevent unnecessary refresh
                }}
                className="w-7 h-7 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Compact Tab Navigation */}
            <div className="flex space-x-1 bg-white bg-opacity-20 rounded-lg p-0.5">
              <button
                onClick={() => {
                  setActiveTab("incoming");
                  localStorage.setItem("lastNotificationCheck", new Date().toISOString());
                  setTimeout(() => fetchNotificationCounts(), 500);
                }}
                className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center space-x-1.5 ${activeTab === "incoming"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-white hover:bg-white hover:bg-opacity-10"
                  }`}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16l-4-4m0 0l4-4m-4 4h18"
                  />
                </svg>
                <span>Incoming</span>
                {incomingRequestCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] leading-none">
                    {incomingRequestCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  setActiveTab("updates");
                  localStorage.setItem("lastNotificationCheck", new Date().toISOString());
                  setTimeout(() => fetchNotificationCounts(), 500);
                }}
                className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center space-x-1.5 ${activeTab === "updates"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-white hover:bg-white hover:bg-opacity-10"
                  }`}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
                <span>Updates</span>
                {responseUpdateCount > 0 && (
                  <span className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] leading-none">
                    {responseUpdateCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Tab Content with controlled height */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === "incoming" ? <Response key={requestRefreshKey} onRequestUpdate={fetchNotificationCounts} /> : <ResponseUpdatesTab key={requestRefreshKey} />}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Function to get date ranges for filtering
   */
  const getDateRange = (range) => {
    const today = new Date();
    const startOfDay = (date) => {
      const newDate = new Date(date);
      newDate.setHours(0, 0, 0, 0);
      return newDate;
    };
    const endOfDay = (date) => {
      const newDate = new Date(date);
      newDate.setHours(23, 59, 59, 999);
      return newDate;
    };

    switch (range) {
      case "today":
        const todayStart = startOfDay(today);
        const todayEnd = endOfDay(today);
        console.log("📅 Today range created:", {
          start: todayStart.toString(),
          end: todayEnd.toString(),
        });
        return { start: todayStart, end: todayEnd };

      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const startOfYesterday = new Date(yesterday.setHours(0, 0, 0, 0));
        const endOfYesterday = new Date(yesterday.setHours(23, 59, 59, 999));
        return { start: startOfYesterday, end: endOfYesterday };

      case "tomorrow":
        // NEW: Tomorrow filter
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const startOfTomorrow = startOfDay(tomorrow);
        const endOfTomorrow = endOfDay(tomorrow);
        console.log("⭐ Tomorrow range created:", {
          start: startOfTomorrow.toString(),
          end: endOfTomorrow.toString(),
        });
        return { start: startOfTomorrow, end: endOfTomorrow };

      case "thisWeek":
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(today);
        endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
        endOfWeek.setHours(23, 59, 59, 999);
        return { start: startOfWeek, end: endOfWeek };

      case "thisMonth":
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0
        );
        endOfMonth.setHours(23, 59, 59, 999);
        return { start: startOfMonth, end: endOfMonth };

      case "reopen":
        return null;

      case "all":
        return null;

      case "custom":
        // Custom date range handling
        if (customDateFrom && customDateTo) {
          return {
            start: startOfDay(new Date(customDateFrom)),
            end: endOfDay(new Date(customDateTo)),
          };
        }
        return null;

      default:
        return null;
    }
  };

  // Handle date range tab click
  const handleDateRangeClick = (tabId) => {
    setFilterDateRange(tabId);
    if (tabId === "custom") {
      setShowCustomDateRange(true);
    } else {
      setShowCustomDateRange(false);
      setCustomDateFrom("");
      setCustomDateTo("");
    }
  };

  // UPDATED: Handle recurring type toggle click (no non-recurring)
  const handleRecurringTypeClick = (tabId) => {
    setFilterRecurringType(tabId);
    setCurrentPage(1); // Reset pagination when filter changes
  };

  // NEW: Reset pagination when recurring type filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterRecurringType]);

  // Custom date range JSX component
  const CustomDateRangeComponent = () => (
    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border-2 border-dashed border-blue-200">
      <div className="flex items-center mb-3">
        <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-2">
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className="text-sm font-bold text-blue-800">Custom Date Range</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* From Date */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-blue-700 uppercase tracking-wide">
            📅 From Date
          </label>
          <div className="relative">
            <input
              type="date"
              value={customDateFrom}
              onChange={(e) => setCustomDateFrom(e.target.value)}
              className="
              w-full px-4 py-3 
              border-2 border-blue-200 rounded-xl 
              focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 
              bg-white hover:border-blue-300 
              transition-all duration-300 
              text-gray-800 font-medium
              cursor-pointer
            "
              max={customDateTo || undefined}
            />
          </div>
        </div>

        {/* To Date */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-purple-700 uppercase tracking-wide">
            📅 To Date
          </label>
          <div className="relative">
            <input
              type="date"
              value={customDateTo}
              onChange={(e) => setCustomDateTo(e.target.value)}
              className="
              w-full px-4 py-3 
              border-2 border-purple-200 rounded-xl 
              focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 
              bg-white hover:border-purple-300 
              transition-all duration-300 
              text-gray-800 font-medium
              cursor-pointer
            "
              min={customDateFrom || undefined}
            />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => {
            const today = new Date();
            const lastWeek = new Date(today);
            lastWeek.setDate(today.getDate() - 7);
            setCustomDateFrom(lastWeek.toISOString().split("T")[0]);
            setCustomDateTo(today.toISOString().split("T")[0]);
          }}
          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200 transition-colors duration-200"
        >
          Last 7 Days
        </button>
        <button
          onClick={() => {
            const today = new Date();
            const lastMonth = new Date(today);
            lastMonth.setDate(today.getDate() - 30);
            setCustomDateFrom(lastMonth.toISOString().split("T")[0]);
            setCustomDateTo(today.toISOString().split("T")[0]);
          }}
          className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-200 transition-colors duration-200"
        >
          Last 30 Days
        </button>
        <button
          onClick={() => {
            setCustomDateFrom("");
            setCustomDateTo("");
          }}
          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors duration-200"
        >
          Clear
        </button>
      </div>

      {/* Selected Range Display */}
      {customDateFrom && customDateTo && (
        <div className="mt-3 p-2 bg-white rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <svg
                  className="w-2 h-2 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span className="text-xs font-medium text-green-800">
                Range: {new Date(customDateFrom).toLocaleDateString()} →{" "}
                {new Date(customDateTo).toLocaleDateString()}
              </span>
            </div>
            <span className="text-xs text-gray-500">
              {Math.ceil(
                (new Date(customDateTo) - new Date(customDateFrom)) /
                (1000 * 60 * 60 * 24)
              ) + 1}{" "}
              days
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const getUserInfo = useCallback(() => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return null;
      const decoded = jwtDecode(token);
      return {
        id: decoded.id,
        role: decoded.role,
        companyId: decoded.companyId,
      };
    } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
    }
  }, []);

  // UPDATED: fetchTasks - Add deduping by _id to prevent accumulation/duplicates
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = authToken;
      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }
      const userInfo = getUserInfo();
      if (!userInfo) {
        throw new Error("Failed to get user info from token.");
      }
      let url = "";
      if (userInfo.role === "Manager" || userInfo.role === "company") {
        // Manager: fetch all tasks (includes instances only for recurring, per backend)
        url = `${API_BASE_URL}/task/tasks`;
      } else {
        url = `${API_BASE_URL}/task/tasks/employee/${userInfo.id}`;
      }
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "x-auth-token": token,
        },
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.clear();
          throw new Error("Authentication failed. Please log in again.");
        }
        throw new Error(`Failed to fetch tasks`);
      }
      const data = await response.json();
      let taskList = [];
      if (Array.isArray(data)) {
        taskList = data;
      } else if (data && Array.isArray(data.tasks)) {
        taskList = data.tasks;
      } else if (data && Array.isArray(data.data)) {
        taskList = data.data;
      }

      // NEW: Dedupe tasks by _id to prevent duplicates (e.g., from backend or multiple fetches)
      const uniqueTasks = [
        ...new Map(taskList.map((task) => [task._id, task])).values(),
      ];
      console.log(
        `🔄 Fetched ${taskList.length} tasks, deduped to ${uniqueTasks.length} unique tasks`
      ); // Debug log (remove in prod)

      // NEW: Log for debugging recurring (remove in production if needed)
      const recurringParents = uniqueTasks.filter(
        (t) => t.repeat && !t.isRecurringInstance
      );
      const instances = uniqueTasks.filter((t) => t.isRecurringInstance);
      console.log(
        `📊 Recurring: ${recurringParents.length} parents, ${instances.length} instances`
      ); // Debug

      setTasks(uniqueTasks); // FIXED: Set deduped list
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [authToken, getUserInfo]);

  const fetchShiftedTaskIds = useCallback(async () => {
    try {
      const token = authToken;
      if (!token) return;

      const response = await fetch(
        `${API_BASE_URL}/taskstatusupdate/reassigned-tasks?limit=1000`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-auth-token": token,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const reassigned = data.reassignedTasks || data.data || [];
        const ids = new Set();

        reassigned.forEach(item => {
          // Check all possible places the task ID might be
          if (item.task) {
            if (typeof item.task === 'string') ids.add(item.task);
            else if (item.task._id) ids.add(String(item.task._id));
            else if (item.task.id) ids.add(String(item.task.id));
          }
          if (item.taskId) ids.add(String(item.taskId));
          if (item._id && !item.task) ids.add(String(item._id)); // Fallback
        });

        console.log(`⚙️ Found ${ids.size} shifted task IDs from history`);
        setShiftedTaskIds(ids);
      }
    } catch (error) {
      console.error("Error fetching shifted tasks history:", error);
    }
  }, [authToken]);

  // Handle task notification clicked and open detail modal
  useEffect(() => {
    const clickedNotificationStr = localStorage.getItem('clickedTaskNotification');
    if (clickedNotificationStr && tasks.length > 0) {
      try {
        const notification = JSON.parse(clickedNotificationStr);
        localStorage.removeItem('clickedTaskNotification');

        // Find task by ID or Title
        const taskId = notification.meta?.taskId || notification.meta?.id || notification.taskId;
        const taskTitle = notification.meta?.taskTitle || notification.title || notification.message;

        // Try to find the task in tasks list
        let foundTask = tasks.find(t => {
          const tId = String(t._id || t.id || '');
          return taskId && tId && tId === String(taskId);
        });

        if (!foundTask && taskTitle) {
          // Try to find by extracting title from message
          // e.g. "New task assigned: sdbfsdgb | Company: FOR TEST" -> "sdbfsdgb"
          // e.g. "Employee created a new task: tata" -> "tata"
          let cleanTitle = '';
          const msg = notification.message || '';

          if (msg.includes('New task assigned:')) {
            const afterAssigned = msg.split('New task assigned:')[1];
            if (afterAssigned) {
              cleanTitle = afterAssigned.split('|')[0].trim();
            }
          } else if (msg.includes('created a new task:')) {
            cleanTitle = msg.split('created a new task:')[1].trim();
          }

          if (!cleanTitle) {
            if (taskTitle && !taskTitle.toLowerCase().includes('task assigned')) {
              cleanTitle = taskTitle;
            } else if (msg.includes(':')) {
              const parts = msg.split(':');
              if (parts.length > 1) {
                cleanTitle = parts[1].split('|')[0].trim();
              }
            } else {
              cleanTitle = taskTitle;
            }
          }

          if (cleanTitle) {
            foundTask = tasks.find(t =>
              (t.title && t.title.toLowerCase().includes(cleanTitle.toLowerCase())) ||
              (t.description && t.description.toLowerCase().includes(cleanTitle.toLowerCase()))
            );
          }
        }

        if (foundTask) {
          setSelectedTaskDetail(foundTask);
          setShowTaskDetail(true);
        }
      } catch (e) {
        console.error('Error opening task from notification:', e);
      }
    }
  }, [tasks]);

  useEffect(() => {
    if (!hasPermission("list_task")) {
      setTasks([]);
      setLoading(false);
      return;
    }
    fetchTasks();
    fetchShiftedTaskIds();
  }, [accessPermissions, userRole, fetchTasks, fetchShiftedTaskIds]);

  // Global event listener for task synchronization
  useEffect(() => {
    const handleGlobalRefresh = () => {
      console.log("🔄 Global task refresh event received");
      fetchTasks();
    };

    window.addEventListener("taskCreated", handleGlobalRefresh);
    window.addEventListener("taskUpdated", handleGlobalRefresh);

    return () => {
      window.removeEventListener("taskCreated", handleGlobalRefresh);
      window.removeEventListener("taskUpdated", handleGlobalRefresh);
    };
  }, [fetchTasks]);

  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const companyId = await getCompanyId();
      const token = authToken;

      if (!companyId || !token) {
        throw new Error("Missing company ID or authentication token");
      }

      const response = await fetch(
        `${API_BASE_URL}/departments/company/${companyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "x-auth-token": token,
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch departments");
      }

      const data = await response.json();
      const deptList = Array.isArray(data)
        ? data
        : data.data || data.departments || [];
      setDepartments(deptList);
    } catch (error) {
      console.error("Error fetching departments:", error);
      setError("Failed to load departments");
      if (window.toast) {
        window.toast.error("Failed to load departments");
      }
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, authToken, getCompanyId]);

  /**
   * Fetch employees for task assignment details
   */
  const fetchAllEmployees = useCallback(async () => {
    try {
      const companyId = await getCompanyId();
      const token = authToken;

      if (!companyId || !token) return;

      const response = await fetch(
        `${API_BASE_URL}/employees/company/${companyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "x-auth-token": token,
          },
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        const empList = Array.isArray(data)
          ? data
          : data.data || data.employees || [];
        setEmployees(empList.filter(emp => emp.isActive !== false));
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  }, [API_BASE_URL, authToken, getCompanyId]);

  // Enhanced department filter handler
  const handleDepartmentFilterChange = (departmentId) => {
    setFilterDepartment(departmentId);

    // Reset employee filter when department changes
    setFilterEmployee("all");

    // Reset current page to 1 when filters change
    setCurrentPage(1);
  };

  // Fetch employees filtered by selected department
  const fetchEmployeesForDepartmentFilter = useCallback(
    async (departmentId) => {
      if (!departmentId || departmentId === "all") {
        // If no department selected, show all active employees
        setFilteredEmployeesForFilter(employees.filter(emp => emp.isActive !== false));
        return;
      }

      try {
        const companyId = await getCompanyId();
        const token = authToken;

        if (!companyId || !token) {
          setFilteredEmployeesForFilter([]);
          return;
        }

        // Fetch all employees and filter by department
        const response = await fetch(
          `${API_BASE_URL}/employees/company/${companyId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "x-auth-token": token,
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch employees: ${response.status}`);
        }

        const data = await response.json();
        let empList = Array.isArray(data)
          ? data
          : data.data || data.employees || [];

        // Filter employees by selected department
        const departmentFilteredEmployees = empList.filter((emp) => {
          if (emp.isActive === false) return false;
          if (!emp.department) return false;

          if (typeof emp.department === "string") {
            return emp.department === departmentId;
          } else if (Array.isArray(emp.department)) {
            return emp.department.some(
              (dept) =>
                (typeof dept === "string" ? dept : dept._id || dept.id) ===
                departmentId
            );
          } else if (
            typeof emp.department === "object" &&
            emp.department !== null
          ) {
            return (emp.department._id || emp.department.id) === departmentId;
          }

          return false;
        });

        setFilteredEmployeesForFilter(departmentFilteredEmployees);

        // If current selected employee is not in the filtered list, reset employee filter
        if (filterEmployee !== "all") {
          const isEmployeeInDepartment = departmentFilteredEmployees.some(
            (emp) => emp._id === filterEmployee
          );
          if (!isEmployeeInDepartment) {
            setFilterEmployee("all");
          }
        }
      } catch (error) {
        console.error("Error fetching department employees:", error);
        setFilteredEmployeesForFilter([]);
      }
    },
    [API_BASE_URL, authToken, getCompanyId, employees, filterEmployee]
  );

  // Update filtered employees when department filter changes
  useEffect(() => {
    fetchEmployeesForDepartmentFilter(filterDepartment);
  }, [filterDepartment, fetchEmployeesForDepartmentFilter]);

  // Initialize filtered employees with all employees on component mount
  useEffect(() => {
    if (employees.length > 0) {
      setFilteredEmployeesForFilter(employees.filter(emp => emp.isActive !== false));
    }
  }, [employees]);

  // Handler functions

  const handleUpdateStatus = (task) => {
    setSelectedTask(task);
    setShowUpdateStatus(true);
  };

  const handleStatusUpdate = async (updatedTask) => {
    console.log("Task updated:", updatedTask);
    setShowUpdateStatus(false);

    // Optimistic update: Update local state immediately
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task._id === updatedTask._id ? { ...task, ...updatedTask } : task
      )
    );

    // Then refresh from server to ensure consistency
    await fetchTasks();
  };

  const handleRequestClick = () => {
    setShowRequestForm(true);
  };

  const handleRequestSubmit = (requestData) => {
    console.log("Request submitted:", requestData);
    setShowRequestForm(false);
    fetchNotificationCounts();
    if (window.toast) {
      window.toast.success("Request submitted successfully!");
    }
  };

  const handleDeleteTask = async (taskId, isParent = false) => {
    let confirmMsg;
    let confirmTitle;

    if (isParent) {
      confirmTitle = "Delete Recurring Series";
      confirmMsg =
        "This will remove the parent template and ALL instances (past and future). This action cannot be undone.";
    } else {
      confirmTitle = "Delete Task";
      confirmMsg =
        "For recurring instances, only this one will be removed (series continues). Are you sure?";
    }

    // Show confirmation dialog
    setConfirmDialog({
      open: true,
      title: confirmTitle,
      message: confirmMsg,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => executeDeleteTask(taskId, isParent)
    });
  };

  const executeDeleteTask = async (taskId, isParent = false) => {
    setDialogLoading(true);

    try {
      const token = authToken;
      if (!token) {
        throw new Error("No authentication token found");
      }

      let url = `${API_BASE_URL}/task/tasks/${taskId}`;
      if (isParent) {
        url += "?deleteSeries=true"; // Backend deletes parent + all instances
      }

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "x-auth-token": token,
          Accept: "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete task");
      }
      // Optimistic update: remove deleted task (and its instances if parent) from local state
      setTasks((prevTasks) =>
        prevTasks.filter((task) => {
          // Remove the task itself
          if (task._id === taskId) return false;
          // If deleting a parent series, also remove its child instances locally
          if (
            isParent &&
            (task.parentTask === taskId ||
              String(task.parentTask) === String(taskId))
          ) {
            return false;
          }
          return true;
        })
      );

      await fetchTasks(); // Refresh from server for consistency

      setConfirmDialog({ ...confirmDialog, open: false });
      setSnackbar({
        open: true,
        message: isParent
          ? "Recurring series deleted successfully"
          : "Task deleted successfully",
        type: 'success'
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to delete task",
        type: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleEditTask = async (task) => {
    setIsEditMode(true);
    console.log("task----", task);
    console.log("⚙️ Recurring fields:", {
      recurringStartDate: task.recurringStartDate,
      recurringEndDate: task.recurringEndDate,
      repeatDaysOfWeek: task.repeatDaysOfWeek,
      repeatDatesOfMonth: task.repeatDatesOfMonth,
      repeatFrequency: task.repeatFrequency
    });

    try {
      const isInstance = task.isRecurringInstance;
      const isParentTemplate = task.repeat && !isInstance;
      const taskForEdit = {
        id: task._id,
        title: task.title || "",
        description: task.description || "",
        department: (() => {
          const refs = normalizeDepartmentRefs(task.department);
          if (!refs.length) return "";
          const first = refs[0];
          return first.id || getDeptIdByName(first.name) || "";
        })(),
        priority: task.priority || "Medium",
        status: task.status || "pending",

        startDate: task.startDateTime
          ? new Date(task.startDateTime).toISOString().split("T")[0]
          : "",
        startTime: (() => {
          // For recurring parent templates, use recurringStartDate time if available
          if (isParentTemplate && task.recurringStartDate) {
            return new Date(task.recurringStartDate).toTimeString().slice(0, 5);
          }
          return task.startDateTime
            ? new Date(task.startDateTime).toTimeString().slice(0, 5)
            : "09:00";
        })(),
        deadline: task.endDateTime
          ? new Date(task.endDateTime).toISOString().split("T")[0]
          : "",
        endTime: (() => {
          // For recurring parent templates, use recurringEndDate time if available
          if (isParentTemplate && task.recurringEndDate) {
            return new Date(task.recurringEndDate).toTimeString().slice(0, 5);
          }
          return task.endDateTime
            ? new Date(task.endDateTime).toTimeString().slice(0, 5)
            : "18:00";
        })(),

        assignees: Array.isArray(task.assignedTo)
          ? task.assignedTo.map((id) => getEmployeeName(id))
          : task.assignedTo
            ? [getEmployeeName(task.assignedTo)]
            : [],

        // UPDATED: Enable repeat for parents; disable for instances
        repeat: isParentTemplate ? Boolean(task.repeat) : false,
        repeatFrequency: task.repeatFrequency || "daily",
        repeatWeekday: task.repeatDaysOfWeek?.[0] || "monday",
        repeatMonthDay: task.repeatDatesOfMonth?.[0]?.toString() || "1",

        // FIXED: Added repeatWeekdays and repeatMonthDays arrays
        repeatWeekdays: task.repeatDaysOfWeek?.map(day => day.toLowerCase()) || [],
        repeatMonthDays: task.repeatDatesOfMonth?.map(day => day.toString()) || [],

        // FIXED: Added recurringStartDate and recurringEndDate
        recurringStartDate: task.recurringStartDate
          ? new Date(task.recurringStartDate).toISOString().split("T")[0]
          : "",
        recurringEndDate: task.recurringEndDate
          ? new Date(task.recurringEndDate).toISOString().split("T")[0]
          : "",

        repeatEndDate:
          task.nextFinishDateTime || task.endDateTime
            ? new Date(task.nextFinishDateTime || task.endDateTime)
              .toISOString()
              .split("T")[0]
            : "",
        nextFollowUpDate: task.nextFollowUpDateTime
          ? new Date(task.nextFollowUpDateTime).toISOString().split("T")[0]
          : "",

        // Flags for TaskForm
        isRecurringInstance: isInstance,
        isParentTemplate, // NEW: For UI in TaskForm (e.g., show "Editing Series Template")
        parentId: isInstance ? task.parentTask : null,
        recurrenceActive: task.recurrenceActive !== false, // Default true for parents
        newlyGenerated: task.newlyGenerated || false,

        createdAt: task.createdAt,
        updatedAt: task.updatedAt || new Date().toISOString(),
        createdBy: task.createdBy,
        company: task.company,
        branch: task.branch,
        images: task.images || [],
        files: task.files || [],
        audios: task.audios || [],
        attachments: task.attachments || [],

        points: task.points || task.creditPoints || 0,
      };

      console.log('📋 Task data prepared for edit:', {
        repeatWeekdays: taskForEdit.repeatWeekdays,
        repeatMonthDays: taskForEdit.repeatMonthDays,
        recurringStartDate: taskForEdit.recurringStartDate,
        recurringEndDate: taskForEdit.recurringEndDate,
        repeatFrequency: taskForEdit.repeatFrequency
      });

      setTaskFormData(taskForEdit);
      setShowTaskForm(true);
    } catch (error) {
      console.error("Error preparing task for edit:", error);
      if (window.toast) {
        window.toast.error("Failed to open task for editing");
      }
    }
  };

  const handleSortByTaskId = () => {
    if (sortBy === "taskId") {
      // Toggle order if already sorting by taskId
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Switch to sorting by taskId (default to ascending)
      setSortBy("taskId");
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handleCreateTaskClick = () => {
    setIsEditMode(false);
    setTaskFormData(null);
    setShowTaskForm(true);
  };

  const handleTaskFormSubmit = async (taskData) => {
    try {
      // Dispatch global event - this will trigger fetchTasks via the useEffect listener
      window.dispatchEvent(new CustomEvent('taskCreated', { detail: taskData }));

      // Close form immediately for better UX
      setShowTaskForm(false);
      setTaskFormData(null);
      setIsEditMode(false);

      setSnackbar({
        open: true,
        message: `Task ${isEditMode ? "updated" : "created"} successfully!`,
        type: 'success'
      });
    } catch (error) {
      console.error(
        `❌ Error ${isEditMode ? "updating" : "creating"} task:`,
        error
      );
      setSnackbar({
        open: true,
        message: `Failed to ${isEditMode ? "update" : "create"} task.`,
        type: 'error'
      });
    }
  };

  // NEW: handleStopRecurrence - Disable recurrenceActive on parent (stops future instances)
  const handleStopRecurrence = async (taskId) => {
    if (
      !window.confirm(
        "Stop this recurring series? Future instances will no longer generate, but existing instances remain. You can restart later by editing the template."
      )
    ) {
      return;
    }

    try {
      const token = authToken;
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Call backend stopRecurrence route: PUT /tasks/:id/stop-recurrence
      const response = await fetch(
        `${API_BASE_URL}/task/tasks/${taskId}/stop-recurrence`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "x-auth-token": token,
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        let message = "Failed to stop recurrence";
        try {
          const errorData = await response.json();
          if (errorData && errorData.message) {
            message = errorData.message;
          }
        } catch (e) {
          // Response was not JSON (e.g., HTML error page); keep default message
        }
        throw new Error(message);
      }

      await fetchTasks(); // Refresh (parent now shows "Stopped"; no new generations)

      if (window.toast) {
        window.toast.success(
          "Recurring series stopped. No new instances will generate."
        );
      }
    } catch (error) {
      console.error("Error stopping recurrence:", error);
      if (window.toast) {
        window.toast.error("Failed to stop recurrence");
      }
    }
  };

  // NEW: handleResumeRecurrence - Re-enable recurrenceActive on parent (resume future instances)
  const handleResumeRecurrence = async (taskId) => {
    if (
      !window.confirm(
        "Resume this recurring series? Future instances will start generating again based on the template."
      )
    ) {
      return;
    }

    try {
      const token = authToken;
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Call backend resumeRecurrence route: PUT /tasks/:id/resume-recurrence
      const response = await fetch(
        `${API_BASE_URL}/task/tasks/${taskId}/resume-recurrence`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "x-auth-token": token,
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        let message = "Failed to resume recurrence";
        try {
          const errorData = await response.json();
          if (errorData && errorData.message) {
            message = errorData.message;
          }
        } catch (e) {
          // Non-JSON response; keep default
        }
        throw new Error(message);
      }

      await fetchTasks(); // Refresh (parent now active; new instances will generate)

      if (window.toast) {
        window.toast.success(
          "Recurring series resumed. New instances will generate as scheduled."
        );
      }
    } catch (error) {
      console.error("Error resuming recurrence:", error);
      if (window.toast) {
        window.toast.error("Failed to resume recurrence");
      }
    }
  };

  const handleTaskFormClose = (result) => {
    setShowTaskForm(false);
    setTaskFormData(null);
    setIsEditMode(false);

    if (result?.deleted) {
      fetchTasks();
    }
  };

  const getEmployeeName = (employeeRef) => {
    if (!employeeRef) return "Unknown";

    // Extract the ID from the reference
    const id = typeof employeeRef === 'string' ? employeeRef : employeeRef?.id || employeeRef?._id;

    if (!id) {
      // If no ID but we have an object with name fields, use them
      if (typeof employeeRef === 'object' && employeeRef !== null) {
        return (
          employeeRef.teamMemberName ||
          employeeRef.name ||
          `${employeeRef.firstName || ''} ${employeeRef.lastName || ''}`.trim() ||
          "Unknown Employee"
        );
      }
      return "Unknown Employee";
    }

    // Find employee with EXACT ID match
    const employee = employees.find((emp) => {
      // Convert both to strings for comparison
      const empId = String(emp._id || emp.id);
      const searchId = String(id);

      return empId === searchId;
    });

    if (!employee) {
      console.warn(`Employee not found for ID: ${id}`);

      // Try to get from the task's populated data
      if (typeof employeeRef === 'object' && employeeRef !== null) {
        return (
          employeeRef.teamMemberName ||
          employeeRef.name ||
          `${employeeRef.firstName || ''} ${employeeRef.lastName || ''}`.trim() ||
          "Unknown Employee"
        );
      }

      return "Unknown Employee";
    }

    // Return the employee name
    return (
      employee.teamMemberName ||
      `${employee.firstName || ''} ${employee.lastName || ''}`.trim() ||
      employee.name ||
      "Unknown Employee"
    );
  };


  /**
   * Department helper functions
   */
  const isObjectId = (val) =>
    typeof val === "string" && /^[a-fA-F0-9]{24}$/.test(val.trim());

  const normalizeDepartmentRefs = (departmentField) => {
    if (!departmentField) return [];
    const arr = Array.isArray(departmentField)
      ? departmentField
      : [departmentField];
    return arr
      .map((d) => {
        if (typeof d === "string") {
          return isObjectId(d) ? { id: d } : { name: d };
        }
        if (typeof d === "object" && d !== null) {
          const id = d._id || d.id;
          const name = d.name || d.departmentName;
          return { id, name };
        }
        return {};
      })
      .filter((ref) => ref.id || ref.name);
  };

  const getDeptIdByName = (name) => {
    if (!name) return undefined;
    const dept = departments.find((dept) => {
      const n = dept.name || dept.departmentName;
      return n && String(n).toLowerCase() === String(name).toLowerCase();
    });
    return dept?._id;
  };

  const getDeptNameById = (id) => {
    if (!id) return undefined;
    const dept = departments.find((dept) => String(dept._id) === String(id));
    return dept ? dept.name || dept.departmentName : undefined;
  };

  const getDepartmentName = (departmentField) => {
    const refs = normalizeDepartmentRefs(departmentField);
    if (refs.length === 0) return "Unknown Department";
    const names = refs
      .map((ref) => {
        if (ref.name) return ref.name;
        const name = getDeptNameById(ref.id);
        return name || "Unknown Department";
      })
      .filter(Boolean);
    return names.length
      ? Array.from(new Set(names)).join(", ")
      : "Unknown Department";
  };

  const getFilteredAndSortedTasks = useCallback(() => {
    // Helper function to normalize status for filtering
    const normalizeStatus = (status) => {
      if (!status) return "pending";
      const statusLower = status.toLowerCase();

      // Map all completed variants to 'completed'
      if (
        statusLower === "completed" ||
        statusLower === "late-complete" ||
        statusLower === "re-complete" ||
        statusLower === "re-late-complete"
      ) {
        return "completed";
      }

      // Map all in-progress variants to 'in-progress'
      if (
        statusLower === "in-progress" ||
        statusLower === "in-progress(overdue)" ||
        statusLower === "re-in-progress" ||
        statusLower === "re-in-progress(overdue)"
      ) {
        return "in-progress";
      }

      // NEW: Separate reopened status category
      if (statusLower === "reopened" || statusLower === "reopened(overdue)") {
        return "reopened";
      }

      // Map pending (but NOT reopened)
      if (statusLower === "pending") {
        return "pending";
      }

      // Return as-is for other statuses (cancelled, etc.)
      return statusLower;
    };

    // NEW: Early permission filter - Hide recurring parents if no update_task permission
    let allTasks = tasks.filter((task) => {
      const isParentTemplate = task.repeat && !task.isRecurringInstance;
      if (isParentTemplate && !hasPermission("update_task")) {
        return false;
      }
      return true;
    });

    // UPDATED: Recurring type filter
    if (filterRecurringType !== "all") {
      allTasks = allTasks.filter((task) => {
        const isParentTemplate = task.repeat && !task.isRecurringInstance;
        const isInstance = task.isRecurringInstance;
        const isNonRecurring = !task.repeat && !isInstance;
        const isShiftedTask = !!(task.shifted || task.isShifted || task.shiftedBy ||
          task.shifted_by || task.is_shifted || task.was_shifted || task.shifted_at ||
          shiftedTaskIds.has(String(task._id)) || shiftedTaskIds.has(String(task.id)));

        if (filterRecurringType === "recurring") {
          return isParentTemplate;
        }
        if (filterRecurringType === "shifted") {
          return isShiftedTask;
        }
        return true;
      });
    } else {
      allTasks = allTasks.filter((task) => {
        const isParentTemplate = task.repeat && !task.isRecurringInstance;
        const isInstance = task.isRecurringInstance;
        const isNonRecurring = !task.repeat && !isInstance;
        return isNonRecurring || isInstance;
      });
    }

    let filteredTasks = allTasks.filter((task) => {
      const matchesSearch =
        !searchTerm ||
        task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.parentTitle &&
          task.parentTitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (task.isRecurringInstance &&
          task.parentTask &&
          searchTerm.toLowerCase().includes("template"));

      let matchesStatus = true;
      if (filterStatus !== "all") {
        if (filterStatus === "overdue") {
          matchesStatus = isTaskOverdue(task);
        } else if (filterStatus === "reopened") {
          // NEW: Specific handling for reopened filter
          const statusLower = (task.status || "").toLowerCase();
          matchesStatus =
            statusLower === "reopened" || statusLower === "reopened(overdue)";
        } else {
          const normalizedTaskStatus = normalizeStatus(task.status);
          matchesStatus = normalizedTaskStatus === filterStatus;
        }
      }

      const matchesPriority =
        filterPriority === "all" || task.priority === filterPriority;

      let matchesDepartment = true;
      if (filterDepartment !== "all") {
        const refs = normalizeDepartmentRefs(task.department);
        const taskDeptIds = refs
          .map((ref) => ref.id || getDeptIdByName(ref.name))
          .filter(Boolean)
          .map(String);
        matchesDepartment = taskDeptIds.includes(String(filterDepartment));
      }

      let matchesEmployee = true;
      if (filterEmployee !== "all") {
        let taskEmployeeIds = [];

        if (Array.isArray(task.assignedTo)) {
          taskEmployeeIds = task.assignedTo
            .map((assignee) => {
              if (typeof assignee === "string") {
                return assignee;
              } else if (assignee && typeof assignee === "object") {
                return assignee._id || assignee.id;
              }
              return null;
            })
            .filter(Boolean)
            .map(String);
        } else if (task.assignedTo) {
          if (typeof task.assignedTo === "string") {
            taskEmployeeIds = [task.assignedTo];
          } else if (typeof task.assignedTo === "object") {
            const empId = task.assignedTo._id || task.assignedTo.id;
            if (empId) taskEmployeeIds = [String(empId)];
          }
        }

        matchesEmployee = taskEmployeeIds.includes(String(filterEmployee));
      }

      let matchesDateRange = true;
      if (filterDateRange && filterDateRange !== "all") {
        const dateRange = getDateRange(filterDateRange);

        if (filterDateRange === "reopen") {
          // NEW: Updated to match the new separate reopened category
          const statusLower = (task.status || "").toLowerCase();
          matchesDateRange =
            statusLower === "reopened" ||
            statusLower === "reopened(overdue)" ||
            (task.history &&
              task.history.some(
                (h) =>
                  h.type === "statuschange" &&
                  (h.newValue === "reopened" ||
                    h.newValue === "reopened(overdue)")
              ));
        } else if (dateRange && dateRange.start && dateRange.end) {
          const taskDateStr =
            task.nextFollowUpDateTime || task.startDateTime || task.createdAt;

          if (taskDateStr) {
            const taskDate = new Date(taskDateStr);

            if (!isNaN(taskDate.getTime())) {
              const taskDateOnly = new Date(
                taskDate.getFullYear(),
                taskDate.getMonth(),
                taskDate.getDate()
              );
              const startDateOnly = new Date(
                dateRange.start.getFullYear(),
                dateRange.start.getMonth(),
                dateRange.start.getDate()
              );
              const endDateOnly = new Date(
                dateRange.end.getFullYear(),
                dateRange.end.getMonth(),
                dateRange.end.getDate()
              );

              matchesDateRange =
                taskDateOnly >= startDateOnly && taskDateOnly <= endDateOnly;
            } else {
              matchesDateRange = false;
            }
          } else {
            matchesDateRange = false;
          }
        } else {
          matchesDateRange = false;
        }
      }

      return (
        matchesSearch &&
        matchesStatus &&
        matchesPriority &&
        matchesDepartment &&
        matchesEmployee &&
        matchesDateRange
      );
    });

    filteredTasks.sort((a, b) => {
      let aValue, bValue;
      if (sortBy === "taskId") {
        const extractNumber = (taskId) => {
          if (!taskId) return Infinity;
          const match = taskId.match(/T(\d+)/);
          return match ? parseInt(match[1], 10) : Infinity;
        };
        aValue = extractNumber(a.taskId);
        bValue = extractNumber(b.taskId);
      } else if (sortBy === "startDateTime" || sortBy === "endDateTime") {
        aValue = a.isRecurringInstance
          ? new Date(a[sortBy])
          : new Date(a.nextFinishDateTime || a.createdAt);
        bValue = b.isRecurringInstance
          ? new Date(b[sortBy])
          : new Date(b.nextFinishDateTime || b.createdAt);
      } else {
        aValue = a[sortBy];
        bValue = b[sortBy];
        if (sortBy === "createdAt") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filteredTasks;
  }, [
    tasks,
    filterStatus,
    filterPriority,
    filterDepartment,
    filterEmployee,
    filterDateRange,
    filterRecurringType,
    searchTerm,
    sortBy,
    sortOrder,
    hasPermission,
    isTaskOverdue,
    normalizeDepartmentRefs,
    getDeptIdByName,
    getDateRange,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    filterStatus,
    filterPriority,
    filterDepartment,
    filterEmployee,
    filterDateRange,
  ]);

  /**
   * Pagination
   */
  const getPaginatedTasks = () => {
    const filteredTasks = getFilteredAndSortedTasks();
    const startIndex = (currentPage - 1) * tasksPerPage;
    const endIndex = startIndex + tasksPerPage;
    return {
      tasks: filteredTasks.slice(startIndex, endIndex),
      totalTasks: filteredTasks.length,
      totalPages: Math.ceil(filteredTasks.length / tasksPerPage),
    };
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  /**
   * Get priority badge color
   */
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-800 border-red-200";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "low":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadAllData = async () => {
      await Promise.all([
        fetchTasks(),
        fetchDepartments(),
        fetchAllEmployees(),
        fetchNotificationCounts(),
      ]);
    };

    loadAllData();

    // Removed auto-refresh interval to prevent continuous loading
    // Users can manually refresh if needed
  }, []); // Empty dependency array - only run once on mount

  const { tasks: paginatedTasks, totalTasks, totalPages } = getPaginatedTasks();

  // Handler for shift button click
  const handleShiftTask = (task) => {
    setSelectedTaskForShift(task);
    setShowShiftTaskModal(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">
              Error Loading Tasks
            </h3>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={fetchTasks}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-300"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }
  // Add this helper function near the top of TaskDetailModal component
  const formatDateToDDMMYY = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid date';
    }
  };

  const formatDateTimeToDDMMYY = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      const rawHours = date.getHours();
      const ampm = rawHours >= 12 ? 'PM' : 'AM';
      const hours = String(rawHours % 12 || 12).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid date';
    }
  };

  const TaskDetailModal = ({ task, onClose, onUpdateStatus }) => {
    const [creatorName, setCreatorName] = useState("Loading...");
    const [statusHistory, setStatusHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(null);

    // FINAL: TaskDetailsModal - Show company name for ALL roles (including employees)

    useEffect(() => {
      if (!task) return;

      let cancelled = false;

      const resolveCreator = async () => {
        try {
          console.log('⚙️ Task createdBy data:', task.createdBy); // Debug log

          if (!task?.createdBy) {
            if (!cancelled) setCreatorName('Unknown');
            return;
          }

          // Normalize ID from createdBy (string or object)
          const createdByRef = task.createdBy;

          console.log('⚙️ createdByRef type:', typeof createdByRef, createdByRef); // Debug log

          // 1. First check if createdByRef is already populated with name fields from backend
          if (typeof createdByRef === 'object' && createdByRef !== null) {
            // Backend now properly populates this - check in priority order

            // Check for businessName (Company)
            if (createdByRef.businessName) {
              if (!cancelled) setCreatorName(createdByRef.businessName);
              return;
            }

            // Check for teamMemberName (Employee model field)
            if (createdByRef.teamMemberName) {
              if (!cancelled) setCreatorName(createdByRef.teamMemberName);
              return;
            }

            // Check for name field (generic fallback)
            if (createdByRef.name) {
              if (!cancelled) setCreatorName(createdByRef.name);
              return;
            }

            // Check for firstName/lastName (legacy support)
            if (createdByRef.firstName || createdByRef.lastName) {
              const name = `${createdByRef.firstName || ''} ${createdByRef.lastName || ''}`.trim();
              if (name && !cancelled) {
                setCreatorName(name);
                return;
              }
            }
          }

          // If we reach here, the backend didn't populate properly
          if (!cancelled) setCreatorName('Unknown');

        } catch (e) {
          console.error('Creator resolve error:', e);
          if (!cancelled) setCreatorName('Unknown');
        }
      };

      resolveCreator();

      return () => {
        cancelled = true;
      };
    }, [task, task?.createdBy]);


    // Fetch status history
    const fetchStatusHistory = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/taskstatusupdate/${task._id}/status-updates`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
              "x-auth-token": authToken,
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        let historyData = [];

        if (Array.isArray(data)) {
          historyData = data;
        } else if (data.data && Array.isArray(data.data)) {
          historyData = data.data;
        } else if (data.history && Array.isArray(data.history)) {
          historyData = data.history;
        } else if (data.statusUpdates && Array.isArray(data.statusUpdates)) {
          historyData = data.statusUpdates;
        } else if (data.updates && Array.isArray(data.updates)) {
          historyData = data.updates;
        }

        // Filter to only include updates for the current task._id
        historyData = historyData.filter((update) => {
          const isForCurrentTask =
            update.taskId === task._id || update.task === task._id;
          const isNotReassigned = !(
            update.status && update.status.toLowerCase() === "reassigned"
          );
          return isForCurrentTask && isNotReassigned;
        });

        // Sort history by date (newest first)
        const sortedHistory = historyData.sort((a, b) => {
          const dateA = new Date(a.updatedAt || a.createdAt || a.timestamp);
          const dateB = new Date(b.updatedAt || b.createdAt || b.timestamp);
          return dateB - dateA;
        });

        setStatusHistory(sortedHistory);
      } catch (error) {
        console.error("Error fetching status history:", error);
        setStatusHistory([]);
      } finally {
        setLoading(false);
      }
    };

    // Fetch status history on component mount
    useEffect(() => {
      if (task?._id) {
        fetchStatusHistory();
      }
    }, [task?._id]);

    // Helper functions
    const getAttachmentsByType = (attachments, type) => {
      if (!attachments || !Array.isArray(attachments)) return [];
      return attachments.filter((att) => att.type === type);
    };

    const truncateText = (text, maxLength) => {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    };

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    if (!task) return null;

    const statusInfo = getStatusDisplay(task);
    const isOverdue = isTaskOverdue(task);
    // Use task.completedAt when available; otherwise fall back to task.updatedAt (status change time)
    const overdueTime = formatOverdueTime(
      task.endDateTime,
      task.completedAt || task.completedAtDate || task.completedAtTime || task.updatedAt
    );

    // Function to handle attachment click
    const handleAttachmentClick = (attachment) => {
      if (attachment.url) {
        window.open(attachment.url, "_blank");
      }
    };

    const getFileIcon = (fileName) => {
      const extension = fileName.split(".").pop()?.toLowerCase();
      switch (extension) {
        case "pdf":
          return "📄";
        case "doc":
        case "docx":
          return "📝";
        case "xls":
        case "xlsx":
          return "📊";
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
        case "bmp":
        case "svg":
          return "🖼️";
        case "mp4":
        case "avi":
        case "mov":
        case "wmv":
          return "📹";
        case "mp3":
        case "wav":
        case "ogg":
          return "🎶";
        case "zip":
        case "rar":
        case "7z":
          return "📦";
        case "txt":
          return "📄";
        default:
          return "📎";
      }
    };

    const formatFileSize = (bytes) => {
      if (!bytes) return "Unknown size";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-0 sm:p-4">
        <div className="bg-white sm:rounded-2xl shadow-2xl max-w-4xl w-full h-full sm:h-auto max-h-[100vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 sm:px-8 sm:py-6 shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex justify-between items-start sm:block">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Task Details</h2>
                  <p className="text-blue-100 text-xs sm:text-sm">Complete information about the task</p>
                </div>
                <button
                  onClick={onClose}
                  className="sm:hidden w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-colors shrink-0 ml-4"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {/* Task ID Display */}
                <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg">
                  <span className="text-white font-semibold text-xs sm:text-sm">Task ID:</span>
                  <span className="text-white font-mono font-bold text-xs sm:text-sm">{task.taskId || `${String(task._id).slice(-6)}`}</span>
                </div>
                {/* Update Status Button */}
                {!(task.repeat && !task.isRecurringInstance) && (
                  <button
                    onClick={() => {
                      onUpdateStatus(task);
                    }}
                    className="bg-white hover:bg-slate-50 text-blue-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-bold transition-all duration-300 flex items-center shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.12)] active:scale-95 transform focus:outline-none focus:ring-2 focus:ring-white/50"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Update Status
                  </button>
                )}
                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="hidden sm:flex w-8 h-8 bg-white bg-opacity-20 rounded-full items-center justify-center text-white hover:bg-opacity-30 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>


          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-8">
            {/* Basic Information */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8 mb-4 sm:mb-8">
              {/* Left Column */}
              <div className="space-y-4 sm:space-y-6">
                {/* Task Title */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 flex items-center">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Task Information
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                    <h3 className="text-sm font-bold text-slate-900">{task.title || "Untitled Task"}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {task.description || "No description provided"}
                    </p>

                    {/* Created By */}
                    <div className="text-sm mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-500">
                          Created by:
                        </span>
                        <span className="text-gray-800 font-medium flex items-center space-x-2">
                          {creatorName === "Loading..." ? (
                            <>
                              <span className="inline-block w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></span>
                              <span>{creatorName}</span>
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-4 h-4 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                              <span>{creatorName}</span>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status & Priority */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Status
                    </label>
                    <div
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium border ${getStatusColor(
                        statusInfo.originalStatus,
                        task
                      )}`}
                    >
                      {statusInfo.displayText}
                    </div>
                    {statusInfo.recurringInfo && (
                      <div className="mt-2 text-xs text-gray-500 flex items-center">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                        {statusInfo.recurringInfo}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Priority
                    </label>
                    <div
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium ${getPriorityColor(
                        task.priority
                      )}`}
                    >
                      {task.priority || "Medium"}
                    </div>
                  </div>
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Department
                  </label>
                  <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3 border border-gray-200">
                    <span className="text-gray-800 font-medium">
                      {getDepartmentName(task.department)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4 sm:space-y-6">
                {/* Timeline Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2 flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Timeline
                  </h3>
                  <div className="space-y-3">
                    {/* Start Date & Time */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs sm:text-sm font-medium text-green-800">
                            Start Date & Time
                          </p>
                          <p className="text-base sm:text-lg font-bold text-green-900">
                            {task.startDateTime
                              ? new Date(task.startDateTime).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                }
                              )
                              : "Not set"}
                          </p>
                          <p className="text-xs sm:text-sm text-green-700">
                            {task.startDateTime
                              ? new Date(task.startDateTime).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                }
                              )
                              : "Not set"}
                          </p>
                        </div>
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                          <svg
                            className="w-4 h-4 sm:w-5 sm:h-5 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Deadline Date & Time */}
                    <div
                      className={`border rounded-lg p-3 sm:p-4 ${isOverdue
                        ? "bg-red-50 border-red-200"
                        : "bg-orange-50 border-orange-200"
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p
                            className={`text-xs sm:text-sm font-medium ${isOverdue ? "text-red-800" : "text-orange-800"
                              }`}
                          >
                            Deadline {isOverdue && "(OVERDUE)"}
                          </p>
                          <p
                            className={`text-base sm:text-lg font-bold ${isOverdue ? "text-red-900" : "text-orange-900"
                              }`}
                          >
                            {task.endDateTime
                              ? new Date(task.endDateTime).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                }
                              )
                              : "Not set"}
                          </p>
                          <p
                            className={`text-xs sm:text-sm ${isOverdue ? "text-red-700" : "text-orange-700"
                              }`}
                          >
                            {task.endDateTime
                              ? new Date(task.endDateTime).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                }
                              )
                              : "Not set"}
                          </p>
                          {isOverdue && (
                            <p className="text-xs text-red-600 font-semibold mt-1">
                              Overdue by: {overdueTime}
                            </p>
                          )}
                        </div>
                        <div
                          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${isOverdue ? "bg-red-100" : "bg-orange-100"
                            }`}
                        >
                          <svg
                            className={`w-4 h-4 sm:w-5 sm:h-5 ${isOverdue ? "text-red-600" : "text-orange-600"
                              }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Follow-up Date & Time */}
                    {task.nextFollowUpDateTime && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-blue-800">
                              Next Follow-up
                            </p>
                            <p className="text-base sm:text-lg font-bold text-blue-900">
                              {new Date(
                                task.nextFollowUpDateTime
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </p>
                            <p className="text-xs sm:text-sm text-blue-700">
                              {new Date(
                                task.nextFollowUpDateTime
                              ).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </p>
                          </div>
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                            <svg
                              className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Attachments Section - Improved rendering */}
            {(() => {
              const buildFullUrl = (url) => {
                if (!url) return "";
                if (url.startsWith("http")) return url;
                return `${API_BASE_URL.replace(/\/api$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
              };

              const mapByUrl = new Map();

              const addAttachment = (raw, fallbackType) => {
                if (!raw) return;
                const baseUrl = raw.url || raw.path || raw.secure_url || raw;
                const fullUrl = buildFullUrl(baseUrl);
                if (!fullUrl) return;
                if (mapByUrl.has(fullUrl)) return;
                mapByUrl.set(fullUrl, {
                  ...raw,
                  url: fullUrl,
                  type: raw.type || fallbackType,
                });
              };

              (task.attachments || []).forEach((att) => addAttachment(att, att.type || "attachment"));
              (task.images || []).forEach((img) => addAttachment(img, "image"));
              (task.files || []).forEach((file) => addAttachment(file, "file"));
              (task.audios || []).forEach((audio) => addAttachment(audio, "audio"));

              (statusHistory || []).forEach((history) => {
                if (history.image) {
                  addAttachment({ url: history.image, type: "image" }, "image");
                }
                if (history.file) {
                  addAttachment({ url: history.file, type: "file" }, "file");
                }
                if (history.audio) {
                  addAttachment({ url: history.audio, type: "audio" }, "audio");
                }
              });

              const allAttachments = Array.from(mapByUrl.values());

              if (allAttachments.length === 0) {
                return null;
              }

              return (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Attachments
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allAttachments.map((attachment, index) => (
                      <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-center">
                        {attachment.type === "image" && attachment.url ? (
                          <a
                            href={buildFullUrl(attachment.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={buildFullUrl(attachment.url)}
                              alt="attachment"
                              className="w-32 h-32 object-cover rounded border"
                            />
                          </a>
                        ) : attachment.type === "audio" && attachment.url ? (
                          <audio controls src={buildFullUrl(attachment.url)} className="w-full" />
                        ) : attachment.url ? (
                          <div className="flex flex-col items-center">
                            <div className="text-gray-500 text-sm mb-1">
                              {attachment.name || 'Document'}
                            </div>
                            <a
                              href={buildFullUrl(attachment.url)}
                              download={attachment.name || 'document'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download
                            </a>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Assigned Team Members */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                Assigned Team Members
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(Array.isArray(task.assignedTo) ? task.assignedTo : [task.assignedTo])
                  .map((empId, index) => {
                    // Get the employee name for this specific task's assignee
                    const employeeName = getEmployeeName(empId);

                    return (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-xl p-4 border border-gray-200 flex items-center space-x-3"
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {employeeName
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{employeeName}</p>
                          <p className="text-sm text-gray-600">Team Member</p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Status History Section */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3
                  className="text-base font-semibold text-gray-900 flex items-center"
                  style={{ color: "#2C2C2C" }}
                >
                  <svg
                    className="w-5 h-5 mr-2 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Status History
                </h3>
                <button
                  onClick={fetchStatusHistory}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Refresh
                    </>
                  )}
                </button>
              </div>

              <div
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                style={{ background: "#FFFFFF" }}
              >
                {loading ? (
                  <div className="text-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-gray-500" style={{ color: "#6E6E6E" }}>
                      Loading status history...
                    </p>
                  </div>
                ) : statusHistory.length > 0 ? (
                  <>
                    <div className="hidden md:block overflow-auto">
                      <table className="w-full">
                        <thead
                          className="bg-gray-50 border-b border-gray-200"
                          style={{ background: "#F5F6FA" }}
                        >
                          <tr>
                            <th
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              style={{ color: "#6E6E6E" }}
                            >
                              Task
                            </th>
                            <th
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              style={{ color: "#6E6E6E" }}
                            >
                              Status
                            </th>
                            <th
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              style={{ color: "#6E6E6E" }}
                            >
                              Description
                            </th>
                            <th
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              style={{ color: "#6E6E6E" }}
                            >
                              Next Follow-up
                            </th>
                            <th
                              className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                              style={{ color: "#6E6E6E" }}
                            >
                              Images
                            </th>
                            <th
                              className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                              style={{ color: "#6E6E6E" }}
                            >
                              Files
                            </th>
                            <th
                              className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                              style={{ color: "#6E6E6E" }}
                            >
                              Audio
                            </th>
                          </tr>
                        </thead>
                        <tbody
                          className="bg-white divide-y divide-gray-200"
                          style={{ background: "#FFFFFF" }}
                        >
                          {statusHistory.map((history, index) => {
                            const normalizedAttachments = Array.isArray(history.attachments)
                              ? [...history.attachments]
                              : [];

                            const buildFullUrl = (url) => {
                              if (!url) return "";
                              if (url.startsWith("http")) return url;
                              return `${API_BASE_URL.replace(/\/api$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
                            };

                            if (history.image) {
                              normalizedAttachments.push({
                                url: buildFullUrl(history.image),
                                type: "image",
                              });
                            }
                            if (history.file) {
                              normalizedAttachments.push({
                                url: buildFullUrl(history.file),
                                type: "file",
                              });
                            }
                            if (history.audio) {
                              normalizedAttachments.push({
                                url: buildFullUrl(history.audio),
                                type: "audio",
                              });
                            }

                            const imageAttachments = getAttachmentsByType(
                              normalizedAttachments,
                              "image"
                            );
                            const fileAttachments = getAttachmentsByType(
                              normalizedAttachments,
                              "file"
                            );
                            const audioAttachments = getAttachmentsByType(
                              normalizedAttachments,
                              "audio"
                            );

                            return (
                              <tr
                                key={history._id || index}
                                className="hover:bg-gray-50 transition-colors"
                                style={{ background: "#FFFFFF" }}
                              >
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <div
                                    className="text-sm font-medium text-gray-900"
                                    style={{ color: "#2C2C2C" }}
                                  >
                                    {truncateText(
                                      task?.title || "Unknown Task",
                                      30
                                    )}
                                  </div>
                                  <div
                                    className="text-xs text-gray-500"
                                    style={{ color: "#6E6E6E" }}
                                  >
                                    {formatDate(
                                      history.updatedAt ||
                                      history.createdAt ||
                                      history.timestamp
                                    )}
                                  </div>
                                </td>

                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span
                                    className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                      history.status,
                                      task
                                    )}`}
                                  >
                                    {history.status || "Unknown"}
                                  </span>
                                </td>

                                <td className="px-3 py-2">
                                  <div
                                    className="text-sm text-gray-900 max-w-xs"
                                    style={{ color: "#2C2C2C" }}
                                  >
                                    {history.description ? (
                                      <div>
                                        <p
                                          className="truncate"
                                          title={history.description}
                                        >
                                          {truncateText(history.description, 100)}
                                        </p>
                                        {history.description.length > 100 && (
                                          <button
                                            className="text-blue-600 text-xs hover:underline mt-1"
                                            onClick={() =>
                                              setShowAlert(history.description)
                                            }
                                          >
                                            View full
                                          </button>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 italic">
                                        No description
                                      </span>
                                    )}
                                  </div>
                                </td>

                                <td className="px-3 py-2 whitespace-nowrap">
                                  <div
                                    className="text-sm text-gray-900"
                                    style={{ color: "#2C2C2C" }}
                                  >
                                    {history.nextFollowUpDateTime ? (
                                      <div className="bg-blue-50 px-2 py-1 rounded text-blue-800 text-xs">
                                        {formatDate(history.nextFollowUpDateTime)}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 text-xs">
                                        Not set
                                      </span>
                                    )}
                                  </div>
                                </td>

                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                  {imageAttachments.length > 0 ? (
                                    <div className="flex flex-wrap items-center justify-center gap-1">
                                      {imageAttachments.map((img, idx) => {
                                        const url = img.url || img.path || "";
                                        if (!url) return null;
                                        return (
                                          <a
                                            key={idx}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-block"
                                          >
                                            <img
                                              src={url}
                                              alt="attachment"
                                              className="w-10 h-10 object-cover rounded border"
                                            />
                                          </a>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">
                                      -
                                    </span>
                                  )}
                                </td>

                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                  {fileAttachments.length > 0 ? (
                                    <div className="flex flex-col items-center gap-1">
                                      {fileAttachments.map((file, idx) => {
                                        const url = file.url || file.path || "";
                                        if (!url) return null;
                                        const rawName =
                                          file.name ||
                                          file.originalName ||
                                          file.originalname ||
                                          file.filename ||
                                          url.split("/").pop() ||
                                          "File";
                                        const label = truncateText(rawName, 18);
                                        return (
                                          <a
                                            key={idx}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                          >
                                            <span>{getFileIcon(rawName)}</span>
                                            <span>{label}</span>
                                          </a>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">
                                      -
                                    </span>
                                  )}
                                </td>

                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                  {audioAttachments.length > 0 ? (
                                    <div className="flex flex-col items-center gap-1">
                                      {audioAttachments.map((audio, idx) => {
                                        const url = audio.url || audio.path || "";
                                        if (!url) return null;
                                        return (
                                          <audio
                                            key={idx}
                                            controls
                                            src={url}
                                            className="w-32"
                                          />
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">
                                      -
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="block md:hidden space-y-4 p-2 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      {statusHistory.map((history, index) => {
                        const normalizedAttachments = Array.isArray(history.attachments)
                          ? [...history.attachments]
                          : [];

                        const buildFullUrl = (url) => {
                          if (!url) return "";
                          if (url.startsWith("http")) return url;
                          return `${API_BASE_URL.replace(/\/api$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
                        };

                        if (history.image) {
                          normalizedAttachments.push({
                            url: buildFullUrl(history.image),
                            type: "image",
                          });
                        }
                        if (history.file) {
                          normalizedAttachments.push({
                            url: buildFullUrl(history.file),
                            type: "file",
                          });
                        }
                        if (history.audio) {
                          normalizedAttachments.push({
                            url: buildFullUrl(history.audio),
                            type: "audio",
                          });
                        }

                        const imageAttachments = getAttachmentsByType(
                          normalizedAttachments,
                          "image"
                        );
                        const fileAttachments = getAttachmentsByType(
                          normalizedAttachments,
                          "file"
                        );
                        const audioAttachments = getAttachmentsByType(
                          normalizedAttachments,
                          "audio"
                        );

                        return (
                          <div key={history._id || index} className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-250 dark:border-gray-700 shadow-sm space-y-3">
                            <div className="flex items-center justify-between">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(history.status, task)}`}>
                                {history.status || "Unknown"}
                              </span>
                              <span className="text-xs text-gray-500 font-medium">
                                {formatDate(history.updatedAt || history.createdAt || history.timestamp)}
                              </span>
                            </div>

                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Update Note</p>
                              {history.description ? (
                                <div className="text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 p-2.5 rounded-lg border border-gray-100 dark:border-gray-800">
                                  <p className="whitespace-pre-wrap">{history.description}</p>
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400 italic">No description provided</p>
                              )}
                            </div>

                            {history.nextFollowUpDateTime && (
                              <div className="flex items-center justify-between text-xs bg-blue-50 dark:bg-blue-950/20 p-2.5 rounded-lg border border-blue-100 dark:border-blue-900/30">
                                <span className="font-semibold text-blue-800 dark:text-blue-400">Next Follow-up:</span>
                                <span className="font-medium text-blue-900 dark:text-blue-300">{formatDate(history.nextFollowUpDateTime)}</span>
                              </div>
                            )}

                            {normalizedAttachments.length > 0 && (
                              <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-2">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Attachments</p>

                                {imageAttachments.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {imageAttachments.map((img, idx) => {
                                      const url = img.url || img.path || "";
                                      if (!url) return null;
                                      return (
                                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="relative group shrink-0">
                                          <img src={url} alt="attachment" className="w-12 h-12 object-cover rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm" />
                                        </a>
                                      );
                                    })}
                                  </div>
                                )}

                                {fileAttachments.length > 0 && (
                                  <div className="flex flex-col gap-1.5">
                                    {fileAttachments.map((file, idx) => {
                                      const url = file.url || file.path || "";
                                      if (!url) return null;
                                      const rawName = file.name || file.originalName || file.originalname || file.filename || url.split("/").pop() || "File";
                                      return (
                                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-150 dark:border-gray-800 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50/50 transition-colors">
                                          <span className="text-base">{getFileIcon(rawName)}</span>
                                          <span className="truncate flex-1">{rawName}</span>
                                        </a>
                                      );
                                    })}
                                  </div>
                                )}

                                {audioAttachments.length > 0 && (
                                  <div className="space-y-1">
                                    {audioAttachments.map((audio, idx) => {
                                      const url = audio.url || audio.path || "";
                                      if (!url) return null;
                                      return (
                                        <div key={idx} className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-150 dark:border-gray-800">
                                          <audio controls src={url} className="w-full h-8" />
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <svg
                      className="w-12 h-12 text-gray-300 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    <h4
                      className="text-sm font-medium text-gray-900 mb-1"
                      style={{ color: "#2C2C2C" }}
                    >
                      No Status History
                    </h4>
                    <p
                      className="text-gray-500 mb-2 text-xs"
                      style={{ color: "#6E6E6E" }}
                    >
                      No status updates have been recorded for this task yet.
                    </p>
                  </div>
                )}
              </div>

              {statusHistory.length > 0 && (
                <div
                  className="mt-4 text-sm text-gray-600 text-center"
                  style={{ color: "#6E6E6E" }}
                >
                  Showing {statusHistory.length} status update
                  {statusHistory.length !== 1 ? "s" : ""} (newest first)
                </div>
              )}
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Task ID & Created Info */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-3">
                  Task Identification
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Task ID:</span>
                    <span className="font-mono text-gray-800">
                      {task.taskId || `#${String(task._id).slice(-6)}`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="text-gray-800">
                      {formatDateTimeToDDMMYY(task.createdAt)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated:</span>
                    <span className="text-gray-800">
                      {formatDateTimeToDDMMYY(task.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Recurring Information */}
              {(task.repeat || task.isRecurringInstance) && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-700 mb-3">
                    Recurring Information
                  </h4>
                  <div className="space-y-2">
                    {task.isRecurringInstance && (
                      <div className="flex justify-between">
                        <span className="text-blue-600">Instance of:</span>
                        <span className="text-blue-800">Recurring Series</span>
                      </div>
                    )}
                    {task.repeatFrequency && (
                      <div className="flex justify-between">
                        <span className="text-blue-600">Frequency:</span>
                        <span className="text-blue-800 capitalize">
                          {task.repeatFrequency}
                        </span>
                      </div>
                    )}
                    {/* Weekly: show selected days */}
                    {task.repeatFrequency === "weekly" &&
                      task.repeatDaysOfWeek &&
                      task.repeatDaysOfWeek.length > 0 && (
                        <div className="flex flex-col gap-1">
                          <span className="text-blue-600">Days:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                              const active = task.repeatDaysOfWeek.map(d => d.toLowerCase()).includes(day);
                              return (
                                <span
                                  key={day}
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${active
                                    ? "bg-blue-600 text-white"
                                    : "bg-blue-100 text-blue-400"
                                    }`}
                                >
                                  {day.slice(0, 3)}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    {/* Monthly: show selected dates */}
                    {task.repeatFrequency === "monthly" &&
                      task.repeatDatesOfMonth &&
                      task.repeatDatesOfMonth.length > 0 && (
                        <div className="flex flex-col gap-1">
                          <span className="text-blue-600">Dates of Month:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {task.repeatDatesOfMonth
                              .slice()
                              .sort((a, b) => a - b)
                              .map((date) => (
                                <span
                                  key={date}
                                  className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white"
                                >
                                  {date}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                    {task.recurrenceActive !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-blue-600">Recurrence:</span>
                        <span
                          className={`font-semibold ${task.recurrenceActive
                            ? "text-green-600"
                            : "text-red-600"
                            }`}
                        >
                          {task.recurrenceActive ? "Active" : "Stopped"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Spacer for bottom scroll clearance */}
            <div className="h-8 sm:h-12 w-full shrink-0"></div>
          </div>

          {/* Alert Modal for full description */}
          {showAlert && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4">
              <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">Full Description</h3>
                <p className="text-gray-700 mb-4 whitespace-pre-wrap">
                  {showAlert}
                </p>
                <button
                  onClick={() => setShowAlert(null)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-2 tasks-page-container">
      <style>{`
        @media (max-width: 640px) {
          /* Force dropdown menu to remain within screen boundaries */
          .mobile-fut-dropdown-menu {
            left: 0 !important;
            right: 0 !important;
            width: auto !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          /* Ensure text truncates and does not force expansion */
          .mobile-fut-dropdown-item span {
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            max-width: 80% !important;
            display: inline-block !important;
          }
        }
      `}</style>

      <div className="w-full">
        {/* Header */}
        <div className="mb-0 sm:mb-4">


          {/* ===== DESKTOP: Blue gradient card (sm and above) ===== */}
          <div className="hidden sm:block bg-gradient-to-br from-[#0c1a4e] via-[#1a3a8f] to-[#1e56c8] rounded-2xl shadow-lg border border-blue-400/30 p-4 mb-4 backdrop-blur-sm relative overflow-hidden">
            {/* Glow orbs decorative */}
            <div style={{ position: "absolute", top: "-30px", right: "-20px", width: "120px", height: "120px", borderRadius: "50%", background: "radial-gradient(circle, rgba(96,165,250,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", bottom: "-20px", left: "-10px", width: "100px", height: "100px", borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />

            {/* Desktop Row: Title + All Buttons */}
            <div className="flex items-center justify-between mb-3 relative z-10">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </div>
                <h2 className="text-base font-bold text-white tracking-wide">Filters &amp; Search</h2>
              </div>
              <div className="flex items-center gap-2">
                <EnhancedNotificationBell />
                <button onClick={handleRequestClick} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-3 py-1.5 rounded-xl flex items-center space-x-1.5 transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg border border-green-500/50">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                  <span>New Request</span>
                </button>
                {hasPermission("shifted_task_history") && (
                  <button onClick={() => setShowShiftedTasksHistory(true)} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-3 py-1.5 rounded-xl flex items-center space-x-1.5 transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg border border-pink-500/50">
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                    <span>Shifted Tasks</span>
                  </button>
                )}
                <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg text-white border ${showFilters ? "bg-white/20 border-white/30 backdrop-blur-md" : "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 border-indigo-400/50"}`}>
                  <span>{showFilters ? "Hide" : "Show"} Filters</span>
                  <svg className={`w-4 h-4 shrink-0 transition-transform duration-300 ${showFilters ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                </button>
              </div>
            </div>
            {/* Desktop search */}
            <div className="flex items-center border border-white/20 rounded-xl bg-white/10 backdrop-blur-md shadow-sm focus-within:bg-white/20 focus-within:border-white/40 transition-all duration-200 relative z-10">
              <span className="pl-3 pr-2 text-blue-200 shrink-0">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></svg>
              </span>
              <input type="text" placeholder="Search tasks by title or description..." className="flex-1 py-2.5 pr-4 text-sm bg-transparent outline-none placeholder-blue-200/70 text-white" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>

          {/* ===== MOBILE: Blue gradient card (below sm) ===== */}
          <div className="sm:hidden mb-4 -mx-2 -mt-3 p-4 bg-gradient-to-br from-[#0c1a4e] via-[#1a3a8f] to-[#1e56c8] shadow-lg border-b border-blue-400/30 relative overflow-hidden">
            {/* Glow orbs decorative */}
            <div style={{ position: "absolute", top: "-30px", right: "-20px", width: "120px", height: "120px", borderRadius: "50%", background: "radial-gradient(circle, rgba(96,165,250,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", bottom: "-20px", left: "-10px", width: "100px", height: "100px", borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />

            {/* Row 1: Icon + Title + Bell */}
            <div className="flex items-center justify-between mb-3 relative z-10">
              <div className="flex items-center space-x-2.5">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 shadow-lg">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-white tracking-wide whitespace-nowrap">
                    Filters &amp; Search
                  </h2>
                  <p className="text-[10px] font-medium text-blue-200">Task control center</p>
                </div>
              </div>
              <div className="flex items-center">
                <EnhancedNotificationBell />
              </div>
            </div>

            {/* Row 2: Action Buttons — 3-col grid */}
            <div className="grid grid-cols-3 gap-2 mb-3 relative z-10">
              {/* New Request */}
              <button
                onClick={() => {
                  setShowFilters(false);
                  setShowShiftedTasksHistory(false);
                  handleRequestClick();
                }}
                className={`flex items-center justify-center space-x-1 py-2.5 rounded-xl text-xs font-bold active:scale-95 transition-all duration-200 shadow-sm border ${showRequestForm ? "bg-white/30 text-white border-white/50 backdrop-blur-md" : "bg-white/10 hover:bg-white/20 text-white border-white/20 backdrop-blur-md"}`}
              >
                <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                <span>Request</span>
              </button>

              {/* Shifted Tasks */}
              {hasPermission("shifted_task_history") ? (
                <button
                  onClick={() => {
                    setShowFilters(false);
                    setShowRequestForm(false);
                    setShowShiftedTasksHistory(true);
                  }}
                  className={`flex items-center justify-center space-x-1 py-2.5 rounded-xl text-xs font-bold active:scale-95 transition-all duration-200 shadow-sm border ${showShiftedTasksHistory ? "bg-white/30 text-white border-white/50 backdrop-blur-md" : "bg-white/10 hover:bg-white/20 text-white border-white/20 backdrop-blur-md"}`}
                >
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                  <span>Shifted</span>
                </button>
              ) : (
                <div />
              )}

              {/* Show/Hide Filters */}
              <button
                onClick={() => {
                  const nextVal = !showFilters;
                  setShowFilters(nextVal);
                  if (nextVal) {
                    setShowRequestForm(false);
                    setShowShiftedTasksHistory(false);
                  }
                }}
                className={`flex items-center justify-center space-x-1 py-2.5 rounded-xl text-xs font-bold active:scale-95 transition-all duration-200 shadow-sm border ${showFilters ? "bg-white/30 text-white border-white/50 backdrop-blur-md" : "bg-white/10 hover:bg-white/20 text-white border-white/20 backdrop-blur-md"}`}
              >
                <span>{showFilters ? "Hide" : "Filters"}</span>
                <svg className={`w-3.5 h-3.5 shrink-0 transition-transform duration-300 ${showFilters ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
              </button>
            </div>

            {/* Search Bar */}
            <div className="flex items-center border border-white/20 rounded-xl bg-white/10 backdrop-blur-md shadow-sm focus-within:bg-white/20 focus-within:border-white/40 transition-all duration-200 relative z-10">
              <span className="pl-3 pr-2 text-blue-200 shrink-0">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></svg>
              </span>
              <input
                type="text"
                placeholder="Search tasks by title or description..."
                className="flex-1 py-2.5 pr-4 text-xs outline-none bg-transparent font-medium text-white placeholder-blue-200/70"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Shared: Collapsible Filter Section */}
          {showFilters && (
            <div className="bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-2xl shadow-lg border border-gray-200/50 p-3 sm:p-4 mb-4 animate-fadeIn mobile-fut-filters">
              <div className="space-y-4 sm:space-y-5">
                {/* Quick Filter Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2 flex items-center">
                      <span className="hidden sm:inline mr-1">📊</span>
                      <span className="inline sm:hidden mr-1">
                        <svg className="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2" />
                        </svg>
                      </span>
                      Status
                    </label>

                    {/* Desktop Native Select */}
                    <select
                      className="hidden sm:block w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-300 transition-all duration-200 shadow-sm font-semibold text-gray-700"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="reopened">Reopened</option>
                      <option value="overdue">Overdue</option>
                    </select>

                    {/* Mobile Custom Dropdown */}
                    <div className="relative sm:hidden z-30">
                      <button
                        type="button"
                        onClick={() => {
                          setShowStatusDropdown(!showStatusDropdown);
                          setShowPriorityDropdown(false);
                          setShowDeptDropdown(false);
                          setShowEmpDropdown(false);
                          setShowSortDropdown(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 border rounded-xl transition-all duration-300 text-left mobile-fut-dropdown-btn
                          ${showStatusDropdown ? 'border-blue-400 ring-2 ring-blue-400/20' : 'border-blue-400/50'}`}
                      >
                        <span className="text-xs font-semibold capitalize text-gray-700">
                          {filterStatus === 'all' ? 'All Status' : filterStatus.replace('-', ' ')}
                        </span>
                        <svg className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-200 ${showStatusDropdown ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>

                      {showStatusDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowStatusDropdown(false)} />
                          <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border-2 border-gray-150 rounded-xl shadow-xl z-50 max-h-56 overflow-y-auto py-1 animate-fadeIn mobile-fut-dropdown-menu">
                            {['all', 'pending', 'in-progress', 'completed', 'reopened', 'overdue'].map((statusOption) => {
                              const isSelected = filterStatus === statusOption;
                              return (
                                <div
                                  key={statusOption}
                                  onClick={() => {
                                    setFilterStatus(statusOption);
                                    setShowStatusDropdown(false);
                                  }}
                                  className={`px-3 py-2.5 text-xs font-semibold transition-colors cursor-pointer flex items-center justify-between capitalize border-b border-gray-100 last:border-0 mobile-fut-dropdown-item
                                    ${isSelected ? 'bg-blue-50 text-blue-700 font-bold active-item' : 'text-gray-700 hover:bg-slate-50'}`}
                                >
                                  <span>{statusOption === 'all' ? 'All Status' : statusOption.replace('-', ' ')}</span>
                                  {isSelected && (
                                    <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2 flex items-center">
                      <span className="hidden sm:inline mr-1">🎯</span>
                      <span className="inline sm:hidden mr-1">
                        <svg className="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21v11h-7.5l-1-1H3" />
                        </svg>
                      </span>
                      Priority
                    </label>

                    {/* Desktop Native Select */}
                    <select
                      className="hidden sm:block w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-300 transition-all duration-200 shadow-sm font-semibold text-gray-700"
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                    >
                      <option value="all">All Priority</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>

                    {/* Mobile Custom Dropdown */}
                    <div className="relative sm:hidden z-25">
                      <button
                        type="button"
                        onClick={() => {
                          setShowPriorityDropdown(!showPriorityDropdown);
                          setShowStatusDropdown(false);
                          setShowDeptDropdown(false);
                          setShowEmpDropdown(false);
                          setShowSortDropdown(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 border rounded-xl transition-all duration-300 text-left mobile-fut-dropdown-btn
                          ${showPriorityDropdown ? 'border-blue-400 ring-2 ring-blue-400/20' : 'border-blue-400/50'}`}
                      >
                        <span className="text-xs font-semibold capitalize text-gray-700">
                          {filterPriority === 'all' ? 'All Priority' : filterPriority}
                        </span>
                        <svg className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-200 ${showPriorityDropdown ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>

                      {showPriorityDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowPriorityDropdown(false)} />
                          <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border-2 border-gray-150 rounded-xl shadow-xl z-50 max-h-56 overflow-y-auto py-1 animate-fadeIn mobile-fut-dropdown-menu">
                            {['all', 'high', 'medium', 'low'].map((prioOption) => {
                              const isSelected = filterPriority === prioOption;
                              return (
                                <div
                                  key={prioOption}
                                  onClick={() => {
                                    setFilterPriority(prioOption);
                                    setShowPriorityDropdown(false);
                                  }}
                                  className={`px-3 py-2.5 text-xs font-semibold transition-colors cursor-pointer flex items-center justify-between capitalize border-b border-gray-100 last:border-0 mobile-fut-dropdown-item
                                    ${isSelected ? 'bg-blue-50 text-blue-700 font-bold active-item' : 'text-gray-700 hover:bg-slate-50'}`}
                                >
                                  <span>{prioOption === 'all' ? 'All Priority' : prioOption}</span>
                                  {isSelected && (
                                    <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2 flex items-center">
                      <span className="hidden sm:inline mr-1">🏢</span>
                      <span className="inline sm:hidden mr-1">
                        <svg className="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5" />
                        </svg>
                      </span>
                      Department
                    </label>

                    {/* Desktop Native Select */}
                    <select
                      className="hidden sm:block w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-300 transition-all duration-200 shadow-sm font-semibold text-gray-700"
                      value={filterDepartment}
                      onChange={(e) =>
                        handleDepartmentFilterChange(e.target.value)
                      }
                    >
                      <option value="all">All Departments</option>
                      {departments.map((dept) => (
                        <option key={dept._id} value={dept._id}>
                          {dept.name || dept.departmentName}
                        </option>
                      ))}
                    </select>

                    {/* Mobile Custom Dropdown */}
                    <div className="relative sm:hidden z-20">
                      <button
                        type="button"
                        onClick={() => {
                          setShowDeptDropdown(!showDeptDropdown);
                          setShowStatusDropdown(false);
                          setShowPriorityDropdown(false);
                          setShowEmpDropdown(false);
                          setShowSortDropdown(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 border rounded-xl transition-all duration-300 text-left mobile-fut-dropdown-btn
                          ${showDeptDropdown ? 'border-blue-400 ring-2 ring-blue-400/20' : 'border-blue-400/50'}`}
                      >
                        <span className="text-xs font-semibold truncate pr-2 text-gray-700">
                          {filterDepartment === 'all'
                            ? 'All Departments'
                            : departments.find(d => d._id === filterDepartment)?.name || departments.find(d => d._id === filterDepartment)?.departmentName || 'All Departments'}
                        </span>
                        <svg className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-200 shrink-0 ${showDeptDropdown ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>

                      {showDeptDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowDeptDropdown(false)} />
                          <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border-2 border-gray-150 rounded-xl shadow-xl z-50 max-h-56 overflow-y-auto py-1 animate-fadeIn mobile-fut-dropdown-menu">
                            <div
                              onClick={() => {
                                handleDepartmentFilterChange('all');
                                setShowDeptDropdown(false);
                              }}
                              className={`px-3 py-2.5 text-xs font-semibold transition-colors cursor-pointer flex items-center justify-between border-b border-gray-100 mobile-fut-dropdown-item
                                ${filterDepartment === 'all' ? 'bg-blue-50 text-blue-700 font-bold active-item' : 'text-gray-700 hover:bg-slate-50'}`}
                            >
                              <span>All Departments</span>
                              {filterDepartment === 'all' && (
                                <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            {departments.map((dept) => {
                              const isSelected = filterDepartment === dept._id;
                              const deptName = dept.name || dept.departmentName;
                              return (
                                <div
                                  key={dept._id}
                                  onClick={() => {
                                    handleDepartmentFilterChange(dept._id);
                                    setShowDeptDropdown(false);
                                  }}
                                  className={`px-3 py-2.5 text-xs font-semibold transition-colors cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-0 mobile-fut-dropdown-item
                                    ${isSelected ? 'bg-blue-50 text-blue-700 font-bold active-item' : 'text-gray-700 hover:bg-slate-50'}`}
                                >
                                  <span>{deptName}</span>
                                  {isSelected && (
                                    <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2 flex items-center">
                      <span className="hidden sm:inline mr-1">👤</span>
                      <span className="inline sm:hidden mr-1">
                        <svg className="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </span>
                      Employee{" "}
                      {filterEmployee !== "all" &&
                        `(${filteredEmployeesForFilter.find(
                          (emp) => emp._id === filterEmployee
                        )?.teamMemberName || "Selected"
                        })`}
                    </label>

                    {/* Desktop Native Select */}
                    <select
                      className="hidden sm:block w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-300 transition-all duration-200 shadow-sm font-semibold text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      value={filterEmployee}
                      onChange={(e) => setFilterEmployee(e.target.value)}
                      disabled={filteredEmployeesForFilter.length === 0}
                    >
                      <option value="all">
                        {filterDepartment === "all"
                          ? "All Employees"
                          : filteredEmployeesForFilter.length === 0
                            ? "No employees in department"
                            : `All Employees (${filteredEmployeesForFilter.length})`}
                      </option>
                      {filteredEmployeesForFilter.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.teamMemberName ||
                            `${emp.firstName || ""} ${emp.lastName || ""
                              }`.trim()}
                        </option>
                      ))}
                    </select>

                    {/* Mobile Custom Dropdown */}
                    <div className="relative sm:hidden z-15">
                      <button
                        type="button"
                        disabled={filteredEmployeesForFilter.length === 0}
                        onClick={() => {
                          setShowEmpDropdown(!showEmpDropdown);
                          setShowStatusDropdown(false);
                          setShowPriorityDropdown(false);
                          setShowDeptDropdown(false);
                          setShowSortDropdown(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 border rounded-xl transition-all duration-300 text-left mobile-fut-dropdown-btn
                          ${filteredEmployeesForFilter.length === 0 ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}
                          ${showEmpDropdown ? 'border-blue-400 ring-2 ring-blue-400/20' : 'border-blue-400/50'}`}
                      >
                        <span className="text-xs font-semibold truncate pr-2 text-gray-700">
                          {filterEmployee === 'all'
                            ? (filterDepartment === 'all' ? 'All Employees' : `All Employees (${filteredEmployeesForFilter.length})`)
                            : filteredEmployeesForFilter.find(e => e._id === filterEmployee)?.teamMemberName ||
                            `${filteredEmployeesForFilter.find(e => e._id === filterEmployee)?.firstName || ""} ${filteredEmployeesForFilter.find(e => e._id === filterEmployee)?.lastName || ""}`.trim() || 'All Employees'}
                        </span>
                        <svg className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-200 shrink-0 ${showEmpDropdown ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>

                      {showEmpDropdown && filteredEmployeesForFilter.length > 0 && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowEmpDropdown(false)} />
                          <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border-2 border-gray-150 rounded-xl shadow-xl z-50 max-h-56 overflow-y-auto py-1 animate-fadeIn mobile-fut-dropdown-menu">
                            <div
                              onClick={() => {
                                setFilterEmployee('all');
                                setShowEmpDropdown(false);
                              }}
                              className={`px-3 py-2.5 text-xs font-semibold transition-colors cursor-pointer flex items-center justify-between border-b border-gray-100 mobile-fut-dropdown-item
                                ${filterEmployee === 'all' ? 'bg-blue-50 text-blue-700 font-bold active-item' : 'text-gray-700 hover:bg-slate-50'}`}
                            >
                              <span>
                                {filterDepartment === 'all'
                                  ? "All Employees"
                                  : `All Employees (${filteredEmployeesForFilter.length})`}
                              </span>
                              {filterEmployee === 'all' && (
                                <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            {filteredEmployeesForFilter.map((emp) => {
                              const isSelected = filterEmployee === emp._id;
                              const empName = emp.teamMemberName || `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
                              return (
                                <div
                                  key={emp._id}
                                  onClick={() => {
                                    setFilterEmployee(emp._id);
                                    setShowEmpDropdown(false);
                                  }}
                                  className={`px-3 py-2.5 text-xs font-semibold transition-colors cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-0 mobile-fut-dropdown-item
                                    ${isSelected ? 'bg-blue-50 text-blue-700 font-bold active-item' : 'text-gray-700 hover:bg-slate-50'}`}
                                >
                                  <span>{empName}</span>
                                  {isSelected && (
                                    <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>

                    {filterDepartment !== "all" &&
                      filteredEmployeesForFilter.length === 0 && (
                        <div className="mt-2 text-xs text-amber-600 flex items-center bg-amber-50 p-2 rounded-lg">
                          <svg
                            className="w-4 h-4 mr-1 text-amber-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                          </svg>
                          No employees found
                        </div>
                      )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2 flex items-center">
                      <span className="hidden sm:inline mr-1">🔄</span>
                      <span className="inline sm:hidden mr-1">
                        <svg className="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                        </svg>
                      </span>
                      Sort By
                    </label>

                    {/* Desktop Native Select */}
                    <select
                      className="hidden sm:block w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-300 transition-all duration-200 shadow-sm font-semibold text-gray-700"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="createdAt">Created Date</option>
                      <option value="endDateTime">Deadline</option>
                      <option value="priority">Priority</option>
                      <option value="title">Title</option>
                    </select>

                    {/* Mobile Custom Dropdown */}
                    <div className="relative sm:hidden z-10">
                      <button
                        type="button"
                        onClick={() => {
                          setShowSortDropdown(!showSortDropdown);
                          setShowStatusDropdown(false);
                          setShowPriorityDropdown(false);
                          setShowDeptDropdown(false);
                          setShowEmpDropdown(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 border rounded-xl transition-all duration-300 text-left mobile-fut-dropdown-btn
                          ${showSortDropdown ? 'border-blue-400 ring-2 ring-blue-400/20' : 'border-blue-400/50'}`}
                      >
                        <span className="text-xs font-semibold capitalize text-gray-700">
                          {sortBy === 'createdAt' ? 'Created Date' : sortBy === 'endDateTime' ? 'Deadline' : sortBy}
                        </span>
                        <svg className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-200 ${showSortDropdown ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>

                      {showSortDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowSortDropdown(false)} />
                          <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border-2 border-gray-150 rounded-xl shadow-xl z-50 max-h-56 overflow-y-auto py-1 animate-fadeIn mobile-fut-dropdown-menu">
                            {[
                              { id: 'createdAt', label: 'Created Date' },
                              { id: 'endDateTime', label: 'Deadline' },
                              { id: 'priority', label: 'Priority' },
                              { id: 'title', label: 'Title' }
                            ].map((sortOption) => {
                              const isSelected = sortBy === sortOption.id;
                              return (
                                <div
                                  key={sortOption.id}
                                  onClick={() => {
                                    setSortBy(sortOption.id);
                                    setShowSortDropdown(false);
                                  }}
                                  className={`px-3 py-2.5 text-xs font-semibold transition-colors cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-0 mobile-fut-dropdown-item
                                    ${isSelected ? 'bg-blue-50 text-blue-700 font-bold active-item' : 'text-gray-700 hover:bg-slate-50'}`}
                                >
                                  <span>{sortOption.label}</span>
                                  {isSelected && (
                                    <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Date Range Tabs - Compact */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-2.5 flex items-center">
                    <span className="hidden sm:inline mr-1">📅</span>
                    <span className="inline sm:hidden mr-1">
                      <svg className="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                    Date Range
                  </label>

                  {/* Ribbon Scrollable Container on Mobile */}
                  <div className="flex overflow-x-auto gap-2 pb-2.5 -mx-3 px-3 scrollbar-none sm:overflow-visible sm:flex-wrap sm:mx-0 sm:px-0">
                    {dateRangeTabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => handleDateRangeClick(tab.id)}
                        className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs font-bold transition-all duration-200 flex items-center space-x-1.5 shadow-sm hover:shadow-md shrink-0
                          ${filterDateRange === tab.id
                            ? "bg-blue-600 text-white shadow-md ring-2 ring-blue-300/30 mobile-fut-tab-btn-selected"
                            : "bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200 mobile-fut-tab-btn-unselected"
                          }`}
                      >
                        <span className="hidden sm:inline text-sm">{tab.icon}</span>
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>

                  {showCustomDateRange && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 animate-fadeIn">
                      <div>
                        <input
                          type="date"
                          value={customDateFrom}
                          onChange={(e) => setCustomDateFrom(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm outline-none text-gray-700 font-semibold mobile-fut-input"
                        />
                      </div>
                      <div>
                        <input
                          type="date"
                          value={customDateTo}
                          onChange={(e) => setCustomDateTo(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm outline-none text-gray-700 font-semibold mobile-fut-input"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Task Type and View Toggle */}
                <div className="flex flex-col sm:flex-row justify-between sm:items-center pt-3 border-t-2 border-gray-200/50 gap-3">
                  <div className="flex items-center space-x-3">
                    <label className="text-xs font-bold text-gray-700 flex items-center">
                      <span className="hidden sm:inline mr-1">📋</span>
                      <span className="inline sm:hidden mr-1">
                        <svg className="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </span>
                      Task Type:
                    </label>
                    <div className="flex space-x-2 overflow-x-auto pb-1 scrollbar-none sm:overflow-visible">
                      {recurringTypeTabs.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => handleRecurringTypeClick(tab.id)}
                          className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs font-bold transition-all duration-200 shadow-sm shrink-0
                            ${filterRecurringType === tab.id
                              ? "bg-blue-600 text-white shadow-md ring-2 ring-blue-300/30 mobile-fut-tab-btn-selected"
                              : "bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200 mobile-fut-tab-btn-unselected"
                            }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-0 sm:mb-4 w-full relative z-20">
            {(() => {
              // Helper function to normalize status for filtering
              const normalizeStatus = (status) => {
                if (!status) return "pending";
                const statusLower = status.toLowerCase();

                // Map all completed variants to 'completed'
                if (
                  statusLower === "completed" ||
                  statusLower === "late-complete" ||
                  statusLower === "re-complete" ||
                  statusLower === "re-late-complete"
                ) {
                  return "completed";
                }

                // Map all in-progress variants to 'in-progress'
                if (
                  statusLower === "in-progress" ||
                  statusLower === "in-progress(overdue)" ||
                  statusLower === "re-in-progress" ||
                  statusLower === "re-in-progress(overdue)"
                ) {
                  return "in-progress";
                }

                // Map reopened to separate category
                if (
                  statusLower === "reopened" ||
                  statusLower === "reopened(overdue)"
                ) {
                  return "reopened";
                }

                // Pending only
                if (statusLower === "pending") {
                  return "pending";
                }

                return statusLower;
              };

              // Get base filtered tasks based on current Task Type selection
              let tasksForCounting = tasks.filter((task) => {
                const isParentTemplate = task.repeat && !task.isRecurringInstance;
                if (isParentTemplate && !hasPermission("update_task")) return false;
                return true;
              });

              // Apply Task Type filter (filterRecurringType)
              if (filterRecurringType === "recurring") {
                tasksForCounting = tasksForCounting.filter(
                  (task) => task.repeat && !task.isRecurringInstance
                );
              } else if (filterRecurringType === "shifted") {
                tasksForCounting = tasksForCounting.filter(
                  (task) => !!(task.shifted || task.isShifted || task.shiftedBy ||
                    task.shifted_by || task.is_shifted || task.was_shifted ||
                    task.shifted_at || shiftedTaskIds.has(String(task._id)) ||
                    shiftedTaskIds.has(String(task.id)))
                );
              } else if (filterRecurringType === "non-recurring") {
                tasksForCounting = tasksForCounting.filter(
                  (task) => !task.repeat && !task.isRecurringInstance
                );
              } else if (filterRecurringType === "instances") {
                tasksForCounting = tasksForCounting.filter(
                  (task) => task.isRecurringInstance
                );
              } else {
                // "all" - Show non-recurring + instances (exclude parent templates)
                tasksForCounting = tasksForCounting.filter((task) => {
                  const isParentTemplate = task.repeat && !task.isRecurringInstance;
                  return !isParentTemplate;
                });
              }

              // Apply other filters (search, priority, department, employee, date range)
              tasksForCounting = tasksForCounting.filter((task) => {
                const matchesSearch =
                  !searchTerm ||
                  task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  task.description?.toLowerCase().includes(searchTerm.toLowerCase());

                const matchesPriority =
                  filterPriority === "all" || task.priority === filterPriority;

                let matchesDepartment = true;
                if (filterDepartment !== "all") {
                  const refs = normalizeDepartmentRefs(task.department);
                  const taskDeptIds = refs
                    .map((ref) => ref.id || getDeptIdByName(ref.name))
                    .filter(Boolean)
                    .map(String);
                  matchesDepartment = taskDeptIds.includes(String(filterDepartment));
                }

                let matchesEmployee = true;
                if (filterEmployee !== "all") {
                  let taskEmployeeIds = [];
                  if (Array.isArray(task.assignedTo)) {
                    taskEmployeeIds = task.assignedTo
                      .map((assignee) => {
                        if (typeof assignee === "string") return assignee;
                        if (assignee && typeof assignee === "object")
                          return assignee._id || assignee.id;
                        return null;
                      })
                      .filter(Boolean)
                      .map(String);
                  } else if (task.assignedTo) {
                    if (typeof task.assignedTo === "string") {
                      taskEmployeeIds = [task.assignedTo];
                    } else if (typeof task.assignedTo === "object") {
                      const empId = task.assignedTo._id || task.assignedTo.id;
                      if (empId) taskEmployeeIds = [String(empId)];
                    }
                  }
                  matchesEmployee = taskEmployeeIds.includes(String(filterEmployee));
                }

                let matchesDateRange = true;
                if (filterDateRange && filterDateRange !== "all") {
                  const dateRange = getDateRange(filterDateRange);
                  if (filterDateRange === "reopen") {
                    const statusLower = (task.status || "").toLowerCase();
                    matchesDateRange =
                      statusLower === "reopened" ||
                      statusLower === "reopened(overdue)" ||
                      (task.history &&
                        task.history.some(
                          (h) =>
                            h.type === "statuschange" &&
                            (h.newValue === "reopened" ||
                              h.newValue === "reopened(overdue)")
                        ));
                  } else if (dateRange && dateRange.start && dateRange.end) {
                    const taskDateStr =
                      task.nextFollowUpDateTime ||
                      task.startDateTime ||
                      task.createdAt;
                    if (taskDateStr) {
                      const taskDate = new Date(taskDateStr);
                      if (!isNaN(taskDate.getTime())) {
                        const taskDateOnly = new Date(
                          taskDate.getFullYear(),
                          taskDate.getMonth(),
                          taskDate.getDate()
                        );
                        const startDateOnly = new Date(
                          dateRange.start.getFullYear(),
                          dateRange.start.getMonth(),
                          dateRange.start.getDate()
                        );
                        const endDateOnly = new Date(
                          dateRange.end.getFullYear(),
                          dateRange.end.getMonth(),
                          dateRange.end.getDate()
                        );
                        matchesDateRange =
                          taskDateOnly >= startDateOnly && taskDateOnly <= endDateOnly;
                      } else {
                        matchesDateRange = false;
                      }
                    } else {
                      matchesDateRange = false;
                    }
                  }
                }

                return (
                  matchesSearch &&
                  matchesPriority &&
                  matchesDepartment &&
                  matchesEmployee &&
                  matchesDateRange
                );
              });

              const stats = [
                {
                  count: tasksForCounting.length,
                  label: "Total",
                  color: "blue",
                  icon: "📋",
                  status: "all",
                  gradient: "from-blue-500 to-cyan-500",
                },
                {
                  count: tasksForCounting.filter((t) => {
                    const normalized = normalizeStatus(t.status);
                    return normalized === "pending";
                  }).length,
                  label: "Pending",
                  color: "yellow",
                  icon: "⌛",
                  status: "pending",
                  gradient: "from-amber-500 to-orange-500",
                },
                {
                  count: tasksForCounting.filter((t) => {
                    const normalized = normalizeStatus(t.status);
                    return normalized === "in-progress";
                  }).length,
                  label: "In Progress",
                  color: "purple",
                  icon: "🔄",
                  status: "in-progress",
                  gradient: "from-purple-500 to-pink-500",
                },
                {
                  count: tasksForCounting.filter((t) => {
                    const normalized = normalizeStatus(t.status);
                    return normalized === "completed";
                  }).length,
                  label: "Completed",
                  color: "green",
                  icon: "✅",
                  status: "completed",
                  gradient: "from-green-500 to-emerald-500",
                },
                {
                  count: tasksForCounting.filter((t) => {
                    const normalized = normalizeStatus(t.status);
                    return normalized === "reopened";
                  }).length,
                  label: "Reopened",
                  color: "indigo",
                  icon: "🔄",
                  status: "reopened",
                  gradient: "from-indigo-500 to-violet-500",
                },
                {
                  count: tasksForCounting.filter((t) => {
                    return (
                      t.endDateTime &&
                      new Date(t.endDateTime) < new Date() &&
                      t.status !== "completed" &&
                      t.status !== "cancelled" &&
                      t.status !== "late-complete" &&
                      t.status !== "re-late-complete" &&
                      t.status !== "re-complete"
                    );
                  }).length,
                  label: "Overdue",
                  color: "red",
                  icon: "⚠️",
                  status: "overdue",
                  gradient: "from-red-500 to-rose-500",
                },
              ];

              const colorClasses = {
                blue: {
                  bg: "bg-gradient-to-br from-blue-50/50 to-cyan-50/50",
                  text: "text-blue-700",
                  border: "border-blue-200/50",
                  activeBg: "bg-gradient-to-br from-blue-100/60 to-cyan-100/60",
                  activeBorder: "border-blue-400",
                  ring: "ring-blue-300",
                },
                yellow: {
                  bg: "bg-gradient-to-br from-amber-50/50 to-orange-50/50",
                  text: "text-amber-700",
                  border: "border-amber-200/50",
                  activeBg: "bg-gradient-to-br from-amber-100/60 to-orange-100/60",
                  activeBorder: "border-amber-400",
                  ring: "ring-amber-300",
                },
                purple: {
                  bg: "bg-gradient-to-br from-purple-50/50 to-pink-50/50",
                  text: "text-purple-700",
                  border: "border-purple-200/50",
                  activeBg: "bg-gradient-to-br from-purple-100/60 to-pink-100/60",
                  activeBorder: "border-purple-400",
                  ring: "ring-purple-300",
                },
                green: {
                  bg: "bg-gradient-to-br from-green-50/50 to-emerald-50/50",
                  text: "text-green-700",
                  border: "border-green-200/50",
                  activeBg: "bg-gradient-to-br from-green-100/60 to-emerald-100/60",
                  activeBorder: "border-green-400",
                  ring: "ring-green-300",
                },
                indigo: {
                  bg: "bg-gradient-to-br from-indigo-50/50 to-violet-50/50",
                  text: "text-indigo-700",
                  border: "border-indigo-200/50",
                  activeBg: "bg-gradient-to-br from-indigo-100/60 to-violet-100/60",
                  activeBorder: "border-indigo-400",
                  ring: "ring-indigo-300",
                },
                red: {
                  bg: "bg-gradient-to-br from-red-50/50 to-rose-50/50",
                  text: "text-red-700",
                  border: "border-red-200/50",
                  activeBg: "bg-gradient-to-br from-red-100/60 to-rose-100/60",
                  activeBorder: "border-red-400",
                  ring: "ring-red-300",
                },
              };

              const mobileStyles = {
                blue: {
                  iconBg: "bg-blue-50 text-blue-600",
                  activeBg: "bg-blue-50/40 border-blue-500 shadow-[0_4px_12px_rgba(59,130,246,0.15)] ring-2 ring-blue-100",
                  text: "text-blue-900",
                  icon: (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                      <line x1="9" y1="9" x2="15" y2="9" />
                      <line x1="9" y1="13" x2="15" y2="13" />
                      <line x1="9" y1="17" x2="15" y2="17" />
                    </svg>
                  )
                },
                yellow: {
                  iconBg: "bg-amber-50 text-amber-600",
                  activeBg: "bg-amber-50/40 border-amber-500 shadow-[0_4px_12px_rgba(245,158,11,0.15)] ring-2 ring-amber-100",
                  text: "text-amber-900",
                  icon: (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  )
                },
                purple: {
                  iconBg: "bg-purple-50 text-purple-600",
                  activeBg: "bg-purple-50/40 border-purple-500 shadow-[0_4px_12px_rgba(168,85,247,0.15)] ring-2 ring-purple-100",
                  text: "text-purple-900",
                  icon: (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                  )
                },
                green: {
                  iconBg: "bg-green-50 text-green-600",
                  activeBg: "bg-green-50/40 border-green-500 shadow-[0_4px_12px_rgba(34,197,94,0.15)] ring-2 ring-green-100",
                  text: "text-green-900",
                  icon: (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  )
                },
                indigo: {
                  iconBg: "bg-indigo-50 text-indigo-600",
                  activeBg: "bg-indigo-50/40 border-indigo-500 shadow-[0_4px_12px_rgba(99,102,241,0.15)] ring-2 ring-indigo-100",
                  text: "text-indigo-900",
                  icon: (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                      <polyline points="3 3 3 8 8 8" />
                    </svg>
                  )
                },
                red: {
                  iconBg: "bg-red-50 text-red-600",
                  activeBg: "bg-red-50/40 border-red-500 shadow-[0_4px_12px_rgba(239,68,68,0.15)] ring-2 ring-red-100",
                  text: "text-red-900",
                  icon: (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  )
                }
              };

              return stats.map((stat) => {
                const colors = colorClasses[stat.color];
                const mobile = mobileStyles[stat.color];

                // Add label suffix based on Task Type filter
                let labelSuffix = "";
                if (filterRecurringType === "recurring") {
                  labelSuffix = " (R)";
                } else if (filterRecurringType === "shifted") {
                  labelSuffix = " (S)";
                } else if (filterRecurringType === "instances") {
                  labelSuffix = " (I)";
                } else if (filterRecurringType === "non-recurring") {
                  labelSuffix = " (NR)";
                }

                const displayLabel = stat.label + labelSuffix;
                const isActive = filterStatus === stat.status;

                return (
                  <div
                    key={stat.label}
                    onClick={() => {
                      setFilterStatus(stat.status);
                      setCurrentPage(1);
                    }}
                    className={`rounded-xl border p-2.5 transition-all duration-300 cursor-pointer relative z-30 hover:z-40 flex flex-col justify-center overflow-hidden
                      ${isActive
                        ? `sm:${colors.activeBg} sm:${colors.activeBorder} sm:ring-2 sm:${colors.ring} ${mobile.activeBg} bg-white/60 backdrop-blur-lg border-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] scale-[1.02]`
                        : `sm:${colors.bg} sm:${colors.border} bg-white/40 backdrop-blur-md border-white/60 shadow-[0_4px_16px_rgba(31,38,135,0.05)] hover:bg-white/50 hover:shadow-[0_8px_32px_rgba(31,38,135,0.1)] hover:-translate-y-1`
                      }`}
                  >
                    <div className="flex items-center justify-between w-full h-full relative z-40">
                      <div className="flex items-center space-x-2 w-full">
                        {/* Desktop Emoji Icon */}
                        <div className="hidden sm:block text-xl shrink-0">
                          {stat.icon}
                        </div>
                        {/* Mobile Modern SVG Icon Bubble */}
                        <div className={`sm:hidden w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${mobile.iconBg}`}>
                          {mobile.icon}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                          <div
                            className={`text-xl font-bold tracking-tight sm:${colors.text} ${mobile.text}`}
                          >
                            {stat.count}
                          </div>
                          <div className="text-[10px] sm:text-[11px] text-gray-600 font-bold leading-tight whitespace-normal sm:truncate break-words w-full block">
                            {displayLabel}
                          </div>
                        </div>
                      </div>
                      {isActive && (
                        <div className="ml-1 shrink-0">
                          <div
                            className={`w-5 h-5 rounded-full bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-md`}
                          >
                            <svg
                              className="w-3 h-3 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>

        </div>

        <div className="p-0 bg-gray-50 min-h-screen">
          <div className="bg-transparent md:bg-white rounded-none md:rounded-[16px] shadow-none md:shadow-lg overflow-hidden border-0 md:border border-gray-200 animate-fadeIn">
            {/* Table Header */}
            <div className="bg-white sm:bg-gradient-to-r sm:from-slate-800 sm:via-slate-700 sm:to-slate-800 px-4 sm:px-8 py-2 border-b border-gray-100 sm:border-b-0">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                {/* Left side - Title and description */}
                <div>
                  <h2 className="text-base sm:text-xl font-extrabold text-slate-900 sm:text-white flex items-center animate-slideInLeft">
                    <svg
                      className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-blue-600 sm:text-white shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    <span className="sm:hidden">Task Dashboard</span>
                    <span className="hidden sm:inline">Task Management Dashboard-Manage and track your team's progress</span>
                  </h2>
                </div>

                {/* ✅ ENHANCED: Improved Shift Tasks Action Bar */}
                {selectedTasks.length > 0 && (
                  <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto space-x-2 sm:space-x-3 bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-300 rounded-xl px-3 sm:px-5 py-2 sm:py-3 shadow-lg animate-slideInRight">
                    {/* Selection Count Badge */}
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center shadow-md">
                        <span className="text-white text-sm font-bold">
                          {selectedTasks.length}
                        </span>
                      </div>
                      <span className="text-slate-700 text-sm font-medium">
                        {selectedTasks.length === 1 ? "task" : "tasks"} selected
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <button
                        onClick={handleShiftTasks}
                        className="flex items-center space-x-1 sm:space-x-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 shadow-md hover:shadow-xl transform hover:scale-105"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                          />
                        </svg>
                        <span>Shift Tasks</span>
                      </button>

                      {/* Clear Selection Button */}
                      <button
                        onClick={() => setSelectedTasks([])}
                        className="text-orange-600 hover:text-orange-800 hover:bg-orange-100 p-2 rounded-lg transition-colors"
                        title="Clear selection"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Table Content - Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {hasPermission("shift_task") && (
                      <th className="px-4 py-2 text-center text-sm font-bold text-slate-700 tracking-wider">
                        {/* ✅ ENHANCED: Better Select All UI */}
                        <div className="flex flex-col items-center space-y-2">
                          <input
                            type="checkbox"
                            checked={
                              selectedTasks.length > 0 &&
                              selectedTasks.length ===
                              getFilteredAndSortedTasks().length
                            }
                            indeterminate={
                              selectedTasks.length > 0 &&
                              selectedTasks.length <
                              getFilteredAndSortedTasks().length
                            }
                            onChange={handleSelectAll}
                            className="w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                          />
                          <span className="text-xs bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                            {selectedTasks.length > 0
                              ? `${selectedTasks.length}/${getFilteredAndSortedTasks().length
                              }`
                              : "Shift task"}
                          </span>
                        </div>
                      </th>
                    )}

                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700 tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSortByTaskId()}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Task id</span>
                        {sortBy === "taskId" && (
                          <svg
                            className={`w-4 h-4 text-slate-600 transition-transform ${sortOrder === "asc" ? "rotate-0" : "rotate-180"
                              }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700 tracking-wider">
                      Task details
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700 tracking-wider">
                      Team
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700 tracking-wider">
                      Timeline
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-bold text-slate-700 tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-2 text-center text-sm font-bold text-slate-700 tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTasks.length === 0 ? (
                    <tr>
                      <td
                        colSpan={hasPermission("shift_task") ? "7" : "6"}
                        className="px-8 py-12 text-center"
                      >
                        <div className="text-gray-500">
                          <svg
                            className="w-16 h-16 mx-auto mb-4 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1"
                              d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {(() => {
                              const allParentsHidden =
                                !hasPermission("update_task") &&
                                tasks.every(
                                  (t) => t.repeat && !t.isRecurringInstance
                                );
                              const isRecurringFilter =
                                filterRecurringType === "recurring";
                              if (allParentsHidden && isRecurringFilter) {
                                return "Recurring Management Restricted";
                              }
                              if (isRecurringFilter) {
                                return "No Recurring Tasks Found";
                              }
                              return searchTerm ||
                                filterStatus !== "all" ||
                                filterPriority !== "all" ||
                                filterDepartment !== "all" ||
                                filterDateRange !== "all"
                                ? "No Tasks Found"
                                : "No Tasks Found";
                            })()}
                          </h3>
                          <p className="text-gray-500">
                            {(() => {
                              const allParentsHidden =
                                !hasPermission("update_task") &&
                                tasks.every(
                                  (t) => t.repeat && !t.isRecurringInstance
                                );
                              const isRecurringFilter =
                                filterRecurringType === "recurring";
                              const hasParentsButNoPermission =
                                !hasPermission("update_task") &&
                                tasks.some(
                                  (t) => t.repeat && !t.isRecurringInstance
                                );
                              if (allParentsHidden && isRecurringFilter) {
                                return "Recurring templates are hidden. You need 'update_task' permissions to view and manage them. Contact your administrator.";
                              }
                              if (isRecurringFilter) {
                                return hasParentsButNoPermission
                                  ? "No visible recurring tasks. Templates require 'update_task' permissions."
                                  : "No recurring tasks (templates or instances) found in your system.";
                              }
                              if (hasParentsButNoPermission) {
                                return "No visible tasks. Recurring templates require 'update_task' permissions to view.";
                              }
                              return searchTerm ||
                                filterStatus !== "all" ||
                                filterPriority !== "all" ||
                                filterDepartment !== "all" ||
                                filterDateRange !== "all"
                                ? "Try adjusting your search or filters"
                                : "Get started by creating your first task";
                            })()}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedTasks.map((task, index) => {
                      // ✅ NEW: Check if task is selected for visual feedback
                      const isSelected = selectedTasks.includes(task._id);
                      const statusInfo = getStatusDisplay(task);

                      return (
                        <tr
                          key={task._id}
                          className={`border-b border-gray-100 transition-all duration-200 cursor-pointer ${isSelected
                            ? "bg-orange-50 border-orange-200 shadow-sm"
                            : "hover:bg-blue-50"
                            }`}
                          onClick={(e) => handleTaskClick(task, e)}
                        >
                          {/* ✅ ENHANCED: Checkbox with better visual feedback */}
                          {hasPermission("shift_task") && (
                            <td
                              className="px-4 py-2 text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) =>
                                    handleTaskSelection(task._id, e)
                                  }
                                  className={`w-5 h-5 rounded focus:ring-2 transition-all ${isSelected
                                    ? "text-orange-600 focus:ring-orange-500"
                                    : "text-indigo-600 focus:ring-indigo-500"
                                    }`}
                                />
                              </div>
                            </td>
                          )}

                          {/* Task ID Column with Priority */}
                          <td className="px-4 py-2">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${getPriorityColor(
                                task.priority
                              )}`}
                            >
                              {task.priority || "medium"}
                            </span>
                            <span className="block text-sm font-bold text-slate-500 mt-0.5">
                              {task.taskId}
                            </span>
                          </td>

                          {/* Task Details Column */}
                          <td className="px-4 py-2">
                            <div className="max-w-xs md:max-w-sm">
                              <h3 className="text-base font-medium text-slate-800 leading-snug">
                                {task.title}
                              </h3>
                              {isSelected && (
                                <span className="inline-flex items-center px-1.5 py-0.5 mt-1 rounded-md text-[10px] font-bold bg-orange-100 text-orange-700 uppercase">
                                  Selected
                                </span>
                              )}
                            </div>
                          </td>


                          {/* Team Column */}
                          <td className="px-4 py-2">
                            <div className="flex flex-col items-start">
                              <div className="flex -space-x-2">
                                {task.assignedTo &&
                                  (Array.isArray(task.assignedTo)
                                    ? task.assignedTo
                                    : [task.assignedTo]
                                  )
                                    .slice(0, 3)
                                    .map((empId, avatarIndex) => (
                                      <div
                                        key={avatarIndex}
                                        className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full border-2 border-white flex items-center justify-center text-white font-bold text-xs shadow-sm"
                                        title={getEmployeeName(empId)}
                                      >
                                        {getEmployeeName(empId)
                                          .split(" ")
                                          .map((n) => n[0])
                                          .join("")
                                          .toUpperCase()}
                                      </div>
                                    ))}
                                {task.assignedTo &&
                                  (Array.isArray(task.assignedTo)
                                    ? task.assignedTo.length
                                    : 1) > 3 && (
                                    <div className="w-8 h-8 bg-gray-400 rounded-full border-2 border-white flex items-center justify-center text-white font-bold text-xs">
                                      +
                                      {(Array.isArray(task.assignedTo)
                                        ? task.assignedTo.length
                                        : 1) - 3}
                                    </div>
                                  )}
                              </div>
                              {statusInfo.isShifted && (
                                <div className="mt-1">
                                  <span className="bg-orange-100 text-orange-700 text-[10px] font-extrabold px-1.5 py-0.5 rounded border border-orange-300 uppercase leading-none shadow-sm">
                                    Shifted
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Timeline Column */}
                          <td className="px-4 py-2 text-xs leading-tight whitespace-nowrap">
                            <div className="flex flex-col space-y-1">
                              <div className="flex items-center space-x-1 text-green-700">
                                <span className="font-bold text-[11px]">S:</span>
                                <span>
                                  {task.startDateTime
                                    ? `${new Date(
                                      task.startDateTime
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                    })} ${new Date(
                                      task.startDateTime
                                    ).toLocaleTimeString("en-US", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    })}`
                                    : "N/A"}
                                </span>
                              </div>
                              <div className={`flex items-center space-x-1 ${isTaskOverdue(task)
                                ? "text-red-700 font-bold"
                                : "text-orange-700"
                                }`}>
                                <span className="font-bold text-[11px]">D:</span>
                                <span>
                                  {task.endDateTime
                                    ? `${new Date(
                                      task.endDateTime
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                    })} ${new Date(
                                      task.endDateTime
                                    ).toLocaleTimeString("en-US", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    })}`
                                    : "N/A"}
                                </span>
                              </div>
                              {/* Integrated Compact Progress Bar */}
                              <div className="pt-1">
                                {(() => {
                                  const getProgress = () => {
                                    const status = task.status?.toLowerCase();
                                    if (["completed", "late-complete", "re-complete", "re-late-complete"].includes(status)) return { p: 100, b: "bg-green-500" };
                                    if (["in-progress", "in-progress(overdue)", "re-in-progress", "re-in-progress(overdue)"].includes(status)) return { p: 50, b: "bg-blue-500" };
                                    if (["reopened", "reopened(overdue)"].includes(status)) return { p: 25, b: "bg-purple-500" };
                                    return { p: 0, b: "bg-gray-400" };
                                  };
                                  const { p, b } = getProgress();
                                  return (
                                    <div className="w-full flex items-center space-x-2">
                                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                                        <div className={`h-full ${b} transition-all duration-500`} style={{ width: `${p}%` }}></div>
                                      </div>
                                      <span className="text-[10px] font-bold text-slate-500">{p}%</span>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </td>

                          {/* Status Column */}
                          <td className="px-4 py-2">
                            {(() => {
                              return (
                                <div className="space-y-2">
                                  <div className="relative flex flex-col items-start space-y-2">
                                    <div className="relative">
                                      <span
                                        className={`px-1.5 py-0.5 rounded-md text-xs font-bold border whitespace-nowrap shadow-sm ${getStatusColor(
                                          statusInfo.originalStatus,
                                          task
                                        )}`}
                                      >
                                        {statusInfo.displayText
                                          .charAt(0)
                                          .toUpperCase() +
                                          statusInfo.displayText.slice(1)}
                                      </span>
                                      {statusInfo.isOverdue && (
                                        <div className="absolute -top-1 -right-1">
                                          <span className="bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full font-bold animate-pulse">
                                            !
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    {statusInfo.recurringInfo && (
                                      <div
                                        className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.isParentTemplate &&
                                          !task.recurrenceActive
                                          ? "bg-gray-100 text-gray-600 border border-gray-300"
                                          : "bg-gray-100 text-gray-600 border border-gray-300"
                                          }`}
                                      >
                                        {statusInfo.isParentTemplate ? (
                                          <svg
                                            className="w-3 h-3"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
                                            />
                                          </svg>
                                        ) : (
                                          <svg
                                            className="w-3 h-3"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                            />
                                          </svg>
                                        )}
                                        <span>
                                          {statusInfo.recurringInfo}
                                          {!task.recurrenceActive &&
                                            statusInfo.isParentTemplate
                                            ? " (Stopped)"
                                            : ""}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  {task.newlyGenerated && (
                                    <div className="flex items-center space-x-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200">
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                      <span>Edited</span>
                                      {task.shifted && (
                                        <span className="ml-1">(Shifted)</span>
                                      )}
                                    </div>
                                  )}
                                  <div className="space-y-1 text-xs">
                                    {statusInfo.isOverdue && (
                                      <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></div>
                                        <span className="font-medium text-red-700">
                                          Overdue:{" "}
                                          {formatOverdueTime(
                                            task.endDateTime,
                                            task.completedAt || task.completedAtDate || task.completedAtTime || task.updatedAt
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </td>

                          {/* Actions Column */}
                          <td className="px-4 py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {/* Update Status - fixed slot */}
                              <div className="flex-shrink-0">
                                {filterRecurringType !== "recurring" ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUpdateStatus(task);
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-300 flex items-center justify-center shadow-sm hover:shadow-md gap-1"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    <span className="whitespace-nowrap">Update Status</span>
                                  </button>
                                ) : (
                                  <div className="w-[100px]" />
                                )}
                              </div>

                              {/* Edit - fixed slot */}
                              <div className="flex-shrink-0">
                                {hasPermission("update_task") &&
                                  task.status !== "completed" &&
                                  task.status !== "late-complete" &&
                                  task.status !== "re-late-complete" &&
                                  task.progress !== 100 ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditTask(task);
                                    }}
                                    className="px-2 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1 font-medium text-xs border border-blue-100"
                                    title="Edit Task"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    <span>Edit</span>
                                  </button>
                                ) : null}
                              </div>

                              {/* Recurrence toggle - only rendered when applicable */}
                              {task.repeat && !task.isRecurringInstance && hasPermission("update_task") && (
                                <div className="flex-shrink-0">
                                  {task.recurrenceActive ? (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleStopRecurrence(task._id); }}
                                      className="px-2 py-1.5 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors flex items-center gap-1 font-medium text-xs border border-yellow-100"
                                      title="Stop Recurrence"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636" />
                                      </svg>
                                      <span>Stop</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleResumeRecurrence(task._id); }}
                                      className="px-2 py-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-1 font-medium text-xs border border-green-100"
                                      title="Resume Recurrence"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v6h6M20 20v-6h-6M5 19A9 9 0 1019 5" />
                                      </svg>
                                      <span>Resume</span>
                                    </button>
                                  )}
                                </div>
                              )}

                              {/* Delete - fixed slot */}
                              <div className="flex-shrink-0">
                                {hasPermission("delete_task") &&
                                  task.status !== "completed" &&
                                  task.status !== "late-complete" &&
                                  task.status !== "re-late-complete" &&
                                  task.progress !== 100 ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteTask(task._id, task.repeat && !task.isRecurringInstance);
                                    }}
                                    className="px-2 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1 font-medium text-xs border border-red-100"
                                    title="Delete Task"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    <span>Delete</span>
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View - Only visible on mobile */}
            <div className="md:hidden flex flex-col gap-0 px-0 pt-0 pb-4">
              {paginatedTasks.length === 0 ? (
                <div className="px-6 py-12 text-center bg-white rounded-2xl border border-gray-150 shadow-sm">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="text-sm font-medium text-gray-600">No Tasks Found</p>
                  <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters</p>
                </div>
              ) : (
                paginatedTasks.map((task) => {
                  const isSelected = selectedTasks.includes(task._id);
                  const statusInfo = getStatusDisplay(task);

                  // Priority Color Custom Logic
                  const getCardPriorityBadge = (priority) => {
                    const pLower = priority?.toLowerCase();
                    if (pLower === "high") {
                      return "bg-red-600 text-white border-red-700";
                    } else if (pLower === "medium") {
                      return "bg-orange-500 text-white border-orange-600";
                    } else if (pLower === "low") {
                      return "bg-green-600 text-white border-green-700";
                    }
                    return "bg-gray-600 text-white border-gray-700";
                  };

                  return (
                    <div
                      key={task._id}
                      onClick={(e) => toggleMobileTaskExpand(task._id, e)}
                      className={`task-mobile-card cursor-pointer !p-3 !mb-2 !rounded-xl ${isSelected ? "is-selected" : ""} ${expandedMobileTaskIds.has(task._id) ? "shadow-md ring-1 ring-blue-500/20" : ""}`}
                    >
                      {/* Compact Header: Checkbox, Details, Chevron */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2.5 flex-1 min-w-0">
                          {/* Checkbox */}
                          {hasPermission("shift_task") && (
                            <div onClick={(e) => e.stopPropagation()} className="mt-0.5 shrink-0">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => handleTaskSelection(task._id, e)}
                                className={`w-4.5 h-4.5 rounded cursor-pointer transition-all ${isSelected ? "text-orange-600 focus:ring-orange-500" : "text-indigo-600 focus:ring-indigo-500"}`}
                              />
                            </div>
                          )}

                          {/* Title & Badges */}
                          <div className="flex flex-col gap-1 min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider border ${getCardPriorityBadge(task.priority)}`}>
                                {task.priority || "medium"}
                              </span>
                              <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                                {task.taskId}
                              </span>
                              {isSelected && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-orange-100 text-orange-700 uppercase tracking-wide">
                                  Selected
                                </span>
                              )}
                            </div>
                            <h3 className="text-sm font-bold text-slate-800 leading-snug line-clamp-2 pr-2">
                              {task.title}
                            </h3>
                          </div>
                        </div>

                        {/* Chevron */}
                        <div className={`p-1 shrink-0 rounded-full transition-colors duration-300 ${expandedMobileTaskIds.has(task._id) ? "bg-blue-50 text-blue-600" : "bg-slate-50 text-slate-400"}`}>
                          <svg className={`w-5 h-5 transition-transform duration-300 ${expandedMobileTaskIds.has(task._id) ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>

                      {/* Expandable Details Section */}
                      {expandedMobileTaskIds.has(task._id) && (
                        <div className="flex flex-col gap-4 mt-4 pt-3 border-t border-slate-100 animate-fadeIn">

                          {/* Team Section */}
                          <div className="flex flex-col gap-1">
                            <span className="task-mobile-subtitle">Team Member</span>
                            <div className="flex flex-col gap-2">
                              {task.assignedTo &&
                                (Array.isArray(task.assignedTo) ? task.assignedTo : [task.assignedTo]).map((empId, idx) => {
                                  const empName = getEmployeeName(empId);
                                  const initials = empName
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .toUpperCase()
                                    .substring(0, 2);
                                  return (
                                    <div key={idx} className="flex items-center gap-2">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 border-2 border-white flex items-center justify-center text-white font-bold text-[10px] shadow-md shrink-0">
                                        {initials}
                                      </div>
                                      <span className="task-mobile-value truncate">{empName}</span>
                                    </div>
                                  );
                                })}
                              {(!task.assignedTo || (Array.isArray(task.assignedTo) && task.assignedTo.length === 0)) && (
                                <span className="text-xs text-slate-500 italic">No team member assigned</span>
                              )}
                            </div>
                            {statusInfo.isShifted && (
                              <div className="mt-1">
                                <span className="inline-block bg-orange-100 text-orange-700 text-[9px] font-extrabold px-2 py-0.5 rounded border border-orange-200 uppercase tracking-wider leading-none shadow-xs">
                                  Shifted Task
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Task Statistics Row */}
                          <div className="task-mobile-stats-block grid grid-cols-2 gap-3 pb-2 border-b border-slate-100">
                            <div className="flex flex-col gap-0.5">
                              <span className="task-mobile-subtitle">Start Date</span>
                              <span className="task-mobile-value">
                                {task.startDateTime
                                  ? `${new Date(task.startDateTime).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })} ${new Date(task.startDateTime).toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })}`
                                  : "N/A"}
                              </span>
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <span className="task-mobile-subtitle">Due Date</span>
                              <span className={`task-mobile-value ${isTaskOverdue(task) ? "text-red-600" : ""}`}>
                                {task.endDateTime
                                  ? `${new Date(task.endDateTime).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })} ${new Date(task.endDateTime).toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })}`
                                  : "N/A"}
                              </span>
                            </div>
                          </div>

                          {/* Progress Section */}
                          <div className="flex flex-col gap-1.5">
                            {(() => {
                              const getProgress = () => {
                                const status = task.status?.toLowerCase();
                                if (["completed", "late-complete", "re-complete", "re-late-complete"].includes(status)) return { p: 100, b: "bg-green-500" };
                                if (["in-progress", "in-progress(overdue)", "re-in-progress", "re-in-progress(overdue)"].includes(status)) return { p: 50, b: "bg-blue-500" };
                                if (["reopened", "reopened(overdue)"].includes(status)) return { p: 25, b: "bg-purple-500" };
                                return { p: 0, b: "bg-gray-400" };
                              };
                              const { p, b } = getProgress();
                              return (
                                <>
                                  <div className="flex justify-between items-center text-xs font-bold text-slate-500">
                                    <span>Progress</span>
                                    <span>{p}%</span>
                                  </div>
                                  <div className="task-mobile-progress-bar w-full">
                                    <div className={`h-full ${b} rounded-full transition-all duration-500`} style={{ width: `${p}%` }}></div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                          {/* Status Section */}
                          <div className="task-mobile-stats-block grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1">
                              <span className="task-mobile-subtitle">Status</span>
                              <div>
                                <span className={`inline-block px-2.5 py-0.5 rounded-md text-[11px] font-bold border shadow-xs ${getStatusColor(statusInfo.originalStatus, task)}`}>
                                  {statusInfo.displayText.charAt(0).toUpperCase() + statusInfo.displayText.slice(1)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="task-mobile-subtitle">Overdue</span>
                              <span className={`text-[11px] font-bold ${statusInfo.isOverdue ? "text-red-600 animate-pulse" : "text-slate-500"}`}>
                                {statusInfo.isOverdue ? (
                                  formatOverdueTime(
                                    task.endDateTime,
                                    task.completedAt || task.completedAtDate || task.completedAtTime || task.updatedAt
                                  )
                                ) : (
                                  "Not overdue"
                                )}
                              </span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-col gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                            {/* View Full Details Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTaskClick(task, e);
                              }}
                              className="w-full py-2.5 text-xs flex items-center justify-center gap-1.5 bg-slate-50 text-slate-700 font-bold border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span>View Full Details</span>
                            </button>

                            {/* Update Status Button (Full Width) */}
                            {filterRecurringType !== "recurring" && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateStatus(task);
                                }}
                                className="task-mobile-btn-primary w-full py-2.5 text-xs flex items-center justify-center gap-1.5"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span>Update Status</span>
                              </button>
                            )}

                            {/* Edit & Delete side-by-side */}
                            <div className="grid grid-cols-2 gap-2">
                              {hasPermission("update_task") &&
                                task.status !== "completed" &&
                                task.status !== "late-complete" &&
                                task.status !== "re-late-complete" &&
                                task.progress !== 100 ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditTask(task);
                                  }}
                                  className="task-mobile-btn-secondary w-full py-2 flex items-center justify-center gap-1 text-xs"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  <span>Edit</span>
                                </button>
                              ) : (
                                <div />
                              )}

                              {hasPermission("delete_task") &&
                                task.status !== "completed" &&
                                task.status !== "late-complete" &&
                                task.status !== "re-late-complete" &&
                                task.progress !== 100 ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTask(task._id, task.repeat && !task.isRecurringInstance);
                                  }}
                                  className="task-mobile-btn-danger w-full py-2 flex items-center justify-center gap-1 text-xs"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  <span>Delete</span>
                                </button>
                              ) : (
                                <div />
                              )}
                            </div>

                            {/* Recurrence Toggle if applicable */}
                            {task.repeat && !task.isRecurringInstance && hasPermission("update_task") && (
                              <div className="w-full mt-1">
                                {task.recurrenceActive ? (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleStopRecurrence(task._id); }}
                                    className="w-full py-2 text-yellow-600 hover:bg-yellow-50 active:scale-95 rounded-xl transition-all duration-200 flex items-center justify-center gap-1.5 font-bold text-xs border border-yellow-100 bg-white shadow-xs"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636" />
                                    </svg>
                                    <span>Stop Recurrence</span>
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleResumeRecurrence(task._id); }}
                                    className="w-full py-2 text-green-600 hover:bg-green-50 active:scale-95 rounded-xl transition-all duration-200 flex items-center justify-center gap-1.5 font-bold text-xs border border-green-100 bg-white shadow-xs"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M5 19A9 9 0 1019 5" />
                                    </svg>
                                    <span>Resume Recurrence</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>


        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 border border-slate-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm sm:text-base font-medium text-slate-600 text-center sm:text-left">
                Showing {(currentPage - 1) * tasksPerPage + 1} to{" "}
                {Math.min(currentPage * tasksPerPage, totalTasks)} of{" "}
                {totalTasks} tasks
              </div>

              <div className="flex items-center justify-center flex-wrap gap-2 mt-2 sm:mt-0">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 2 &&
                      pageNumber <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${currentPage === pageNumber
                          ? "bg-blue-600 text-white"
                          : "text-slate-700 bg-white border border-slate-300 hover:bg-slate-50"
                          }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 3 ||
                    pageNumber === currentPage + 3
                  ) {
                    return (
                      <span
                        key={pageNumber}
                        className="px-2 py-2 text-slate-400"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <EnhancedResponseModal />
      {/* Modals */}
      {showTaskForm && (
        <TaskForm
          key={taskFormData ? `edit-${taskFormData.id}` : "create-new"}
          initialData={taskFormData}
          onSubmit={handleTaskFormSubmit}
          onClose={handleTaskFormClose}
          userDepartments={departments}
          userRole="admin"
        />
      )}

      {showUpdateStatus && (
        <UpdateStatus
          task={selectedTask}
          onClose={() => setShowUpdateStatus(false)}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
      {showShiftedTasksHistory && (
        <ShiftedTasksHistoryModal
          onClose={() => setShowShiftedTasksHistory(false)}
          taskId={selectedTask?._id} // optional filtering
        />
      )}

      {showRequestForm && (
        <Request
          onClose={() => setShowRequestForm(false)}
          onRequestSubmit={handleRequestSubmit}
        />
      )}

      {showShiftTaskModal && selectedTaskForShift && (
        <ShiftTaskModal
          isOpen={showShiftTaskModal}
          onClose={() => {
            setShowShiftTaskModal(false);
            setSelectedTaskForShift(null);
          }}
          tasks={selectedTaskForShift}
          employees={employees}
          onShiftSuccess={handleShiftSuccess}
        />
      )}

      {/* Task Detail Modal */}
      {showTaskDetail && (
        <TaskDetailModal
          task={selectedTaskDetail}
          onClose={handleCloseTaskDetail}
          onUpdateStatus={handleUpdateStatus}
        />
      )}

      {/* Snackbar Component */}
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        duration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        position="top-right"
      />

      {/* Confirm Dialog Component */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        loading={dialogLoading}
        showIcon={true}
      />
    </div>
  );
};

export default TaskDisplay;



